<template>
  <div class="shifts-page">

    <!-- ── Page header ─────────────────────────────────────────────── -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Shift Management</h5>
        <p class="page-sub">Create and assign work shifts to employees</p>
      </div>
      <div style="display:flex;gap:8px;align-items:center;">
        <div class="export-wrap">
          <button class="btn-outline btn-export" @click="exportOpen = !exportOpen">
            <font-awesome-icon icon="download" /> Export <font-awesome-icon icon="chevron-down" />
          </button>
          <div v-if="exportOpen" class="export-menu">
            <button class="export-item" @click="openExportModal('csv'); exportOpen = false">
              <font-awesome-icon icon="file-csv" /> Export CSV
            </button>
            <button class="export-item" @click="openExportModal('pdf'); exportOpen = false">
              <font-awesome-icon icon="file-pdf" /> Export PDF
            </button>
          </div>
        </div>
        <button class="btn-outline" @click="openShiftTypes">
          <font-awesome-icon icon="tags" /> Shift Types
        </button>
        <button class="btn-primary" @click="openCreateShift">
          <font-awesome-icon icon="plus" /> New Shift
        </button>
      </div>
    </div>

    <!-- ── Filter bar ─────────────────────────────────────────────── -->
    <div class="filter-bar">
      <div class="search-wrap">
        <font-awesome-icon icon="search" class="search-ico" />
        <input v-model="filterSearch" type="text" placeholder="Search shifts…" class="search-input" />
      </div>

      <select v-model="filterStatus" class="filter-sel">
        <option v-for="opt in statusOpts" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
      </select>

      <select v-model="filterStaff" class="filter-sel">
        <option v-for="opt in staffOpts" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
      </select>

      <select v-model="filterShiftType" class="filter-sel">
        <option value="">All types</option>
        <option v-for="st in shiftTypes" :key="st.id" :value="st.id">{{ st.name }}</option>
      </select>

      <select v-model="filterLocation" class="filter-sel">
        <option value="">All locations</option>
        <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
      </select>

      <div class="filter-client-wrap">
        <search-select
          :key="filterClientKey"
          v-model="filterClientId"
          url="/settings/customers/all"
          query-param="query"
          label-key="customer_name"
          value-key="id"
          response-key="data"
          placeholder="All clients"
          search-placeholder="Filter by client…"
          :min-chars="1"
          :extra-params="{ is_client: 1 }"
          :initial-label="filterClientLabel"
          @select="onFilterClientSelect"
        />
        <button v-if="filterClientId" class="filter-clear" @click="clearClientFilter" title="Clear">
          <font-awesome-icon icon="times" />
        </button>
      </div>

      <div class="per-page-wrap">
        <label class="per-page-label">Per page:</label>
        <select class="per-page-select" v-model.number="shiftLength" @change="shiftPage = 1; fetchShifts()">
          <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
        </select>
      </div>
      <span class="filter-count">{{ shiftPagination ? shiftPagination.total : filteredShifts.length }} shift(s)</span>

      <div class="view-toggle">
        <button class="view-btn" :class="{ 'view-btn--active': viewMode === 'card' }" @click="viewMode = 'card'" title="Card view">
          <font-awesome-icon icon="th-large" />
        </button>
        <button class="view-btn" :class="{ 'view-btn--active': viewMode === 'table' }" @click="viewMode = 'table'" title="Table view">
          <font-awesome-icon icon="list" />
        </button>
      </div>
    </div>

    <!-- ── Skeleton ────────────────────────────────────────────────── -->
    <div v-if="loading" class="shifts-grid">
      <div v-for="n in 6" :key="n" class="sk-card"></div>
    </div>

    <template v-else>

      <!-- ══ Card view ═══════════════════════════════════════════════ -->
      <div v-if="viewMode === 'card'" class="shifts-grid">

        <div v-if="!filteredShifts.length" class="empty-state">
          <font-awesome-icon icon="clock" class="empty-icon" />
          <p v-if="shifts.length">No shifts match your filters.</p>
          <p v-else>No shifts created yet.</p>
          <button v-if="!shifts.length" class="btn-primary" @click="openCreateShift">Create First Shift</button>
          <button v-else class="btn-outline" @click="resetFilters">Clear filters</button>
        </div>

        <div v-for="shift in filteredShifts" :key="shift.id" class="shift-card" @click="openDetail(shift)">
          <div class="card-head">
            <div class="card-icon"><font-awesome-icon icon="clock" /></div>
            <div class="card-name-block">
              <p class="card-name">
                <span v-if="shift.shift_type" class="type-dot" :style="{ background: shift.shift_type.color }"></span>
                {{ shift.shift_type?.name || shift.name }}
              </p>
              <p v-if="shift.location" class="card-client">
                <font-awesome-icon icon="map-marker-alt" class="card-client-icon" />
                {{ shift.location.name }}
              </p>
              <p v-if="shift.client" class="card-client">
                <font-awesome-icon icon="building" class="card-client-icon" />
                {{ shift.client.customer_name }}
              </p>
              <div class="card-badges">
                <span class="badge" :class="shift.is_active ? 'badge--green' : 'badge--gray'">
                  {{ shift.is_active ? 'Active' : 'Inactive' }}
                </span>
                <span v-if="shift.is_overnight" class="badge badge--night">Overnight</span>
                <span v-if="isUnderstaffed(shift)" class="badge badge--warn">⚠ Understaffed</span>
              </div>
            </div>
            <div class="card-actions" @click.stop>
              <button class="btn-icon-sm" @click="openEditShift(shift)" title="Edit"><font-awesome-icon icon="pen" /></button>
              <button class="btn-icon-sm" @click="toggleStatus(shift)" title="Toggle status">
                <font-awesome-icon :icon="shift.is_active ? 'toggle-on' : 'toggle-off'" />
              </button>
              <button class="btn-icon-sm btn-icon-sm--danger" @click="openDelete(shift)" title="Delete">
                <font-awesome-icon icon="trash" />
              </button>
            </div>
          </div>

          <!-- Times + duration -->
          <div class="shift-times">
            <div class="time-block">
              <span class="time-lbl">Start</span>
              <span class="time-val">{{ fmtShiftTime(shift.start_time) }}</span>
            </div>
            <div class="time-sep">—</div>
            <div class="time-block">
              <span class="time-lbl">End</span>
              <span class="time-val">{{ fmtShiftTime(shift.end_time) }}</span>
            </div>
            <div class="time-block">
              <span class="time-lbl">Break</span>
              <span class="time-val">{{ shift.break_minutes }}m</span>
            </div>
            <div class="time-block time-block--dur">
              <span class="time-lbl">Net</span>
              <span class="time-val time-val--accent">{{ shiftDuration(shift) }}</span>
            </div>
            <div v-if="shift.timezone && shift.timezone !== 'UTC'" class="time-block">
              <span class="time-lbl">TZ</span>
              <span class="time-val time-val--tz">{{ shift.timezone }}</span>
            </div>
          </div>

          <!-- Weekly rota schedule -->
          <div class="week-schedule">
            <div class="week-header">
              <span class="week-title">This week's rota</span>
              <span class="week-range">{{ weekLabel }}</span>
            </div>
            <div class="week-grid">
              <div
                v-for="day in weekSchedule(shift)"
                :key="day.iso"
                class="week-row"
                :class="{
                  'week-row--today':  day.isToday,
                  'week-row--empty':  !day.entries.length,
                }"
              >
                <span class="week-day" :class="day.isToday ? 'week-day--today' : ''">{{ day.label }}</span>
                <span class="week-date-num">{{ day.dateNum }}</span>
                <div v-if="day.entries.length" class="week-emps">
                  <div
                    v-for="entry in day.entries"
                    :key="entry.id"
                    class="week-emp-wrap"
                  >
                    <span class="week-emp-avatar">{{ initials(entry.employee) }}</span>
                    <div class="week-emp-tooltip">
                      <p class="et-name">{{ empFullName(entry.employee) }}</p>
                      <p class="et-row">
                        <span class="et-lbl">Time</span>
                        {{ fmtShiftTime(entry.start_time || shift.start_time) }} – {{ fmtShiftTime(entry.end_time || shift.end_time) }}
                      </p>
                      <p v-if="entry.notes" class="et-row"><span class="et-lbl">Note</span>{{ entry.notes }}</p>
                    </div>
                  </div>
                  <span class="week-time-tag">
                    {{ fmtShiftTime(day.entries[0].start_time || shift.start_time) }} – {{ fmtShiftTime(day.entries[0].end_time || shift.end_time) }}
                  </span>
                </div>
                <span v-else class="week-none">—</span>

                <!-- Staffing status -->
                <span
                  v-if="shift.required_employees"
                  class="week-staff-badge"
                  :class="day.entries.length >= shift.required_employees ? 'week-staff-badge--ok' : 'week-staff-badge--short'"
                  :title="day.entries.length >= shift.required_employees
                    ? `Fully staffed (${day.entries.length}/${shift.required_employees})`
                    : `${shift.required_employees - day.entries.length} short of ${shift.required_employees} needed`"
                >
                  <template v-if="day.entries.length >= shift.required_employees">✓</template>
                  <template v-else>−{{ shift.required_employees - day.entries.length }}</template>
                </span>
              </div>
            </div>
          </div>

          <button
            class="btn-assign"
            :class="{ 'btn-assign--full': !isUnderstaffed(shift) }"
            @click.stop="openAssign(shift)"
          >
            <font-awesome-icon :icon="isUnderstaffed(shift) ? 'user-plus' : 'sync-alt'" />
            {{ isUnderstaffed(shift) ? 'Assign Staff' : 'Reassign' }}
          </button>
        </div>
      </div>

      <!-- ══ Table view ══════════════════════════════════════════════ -->
      <div v-else class="table-wrap">
        <table class="shifts-table">
          <thead>
            <tr>
              <th>Shift / Client</th>
              <th>Time</th>
              <th>Duration</th>
              <th>Break</th>
              <th>This Week's Rota</th>
              <th>Status</th>
              <th class="th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!filteredShifts.length">
              <td colspan="7" class="tbl-empty">
                <font-awesome-icon icon="clock" style="font-size:28px;opacity:.25;display:block;margin:0 auto 8px;" />
                <span v-if="shifts.length">No shifts match your filters — <button class="tbl-link" @click="resetFilters">clear filters</button></span>
                <span v-else>No shifts created yet.</span>
              </td>
            </tr>
            <tr v-for="shift in filteredShifts" :key="shift.id" class="tbl-row tbl-row--click" @click="openDetail(shift)">
              <td>
                <p class="tbl-name">{{ shift.shift_type?.name || shift.name }}</p>
                <p v-if="shift.location" class="tbl-client-name">
                  <font-awesome-icon icon="map-marker-alt" class="tbl-client-icon" />
                  {{ shift.location.name }}
                </p>
                <p v-if="shift.client" class="tbl-client-name">
                  <font-awesome-icon icon="building" class="tbl-client-icon" />
                  {{ shift.client.customer_name }}
                </p>
                <div class="card-badges" style="margin-top:4px;">
                  <span v-if="shift.is_overnight" class="badge badge--night" style="font-size:9px;">Overnight</span>
                  <span v-if="isUnderstaffed(shift)" class="badge badge--warn" style="font-size:9px;">⚠ Understaffed</span>
                </div>
              </td>
              <td class="tbl-time mono">{{ fmtShiftTime(shift.start_time) }} – {{ fmtShiftTime(shift.end_time) }}</td>
              <td class="tbl-dur mono">{{ shiftDuration(shift) }}</td>
              <td class="tbl-muted">{{ shift.break_minutes }}m</td>
              <td @click.stop>
                <div class="tbl-rota-week">
                  <div v-for="day in weekSchedule(shift)" :key="day.iso" class="tbl-rota-day" :class="{ 'tbl-rota-day--today': day.isToday }">
                    <span class="tbl-rota-lbl">{{ day.label }}</span>
                    <span
                      class="tbl-rota-dot"
                      :class="day.entries.length >= (shift.required_employees || 1)
                        ? 'tbl-rota-dot--ok'
                        : day.entries.length > 0
                          ? 'tbl-rota-dot--partial'
                          : 'tbl-rota-dot--none'"
                      :title="day.entries.length
                        ? `${day.entries.map(e => empFullName(e.employee)).join(', ')}`
                        : 'No coverage'"
                    ></span>
                  </div>
                </div>
              </td>
              <td>
                <span class="badge" :class="shift.is_active ? 'badge--green' : 'badge--gray'">
                  {{ shift.is_active ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td @click.stop>
                <div class="tbl-actions">
                  <button class="btn-icon-sm" @click="openDetail(shift)" title="View"><font-awesome-icon icon="eye" /></button>
                  <button class="btn-icon-sm" @click="openEditShift(shift)" title="Edit"><font-awesome-icon icon="pen" /></button>
                  <button class="btn-icon-sm" @click="toggleStatus(shift)" title="Toggle status">
                    <font-awesome-icon :icon="shift.is_active ? 'toggle-on' : 'toggle-off'" />
                  </button>
                  <button class="btn-icon-sm" :class="isUnderstaffed(shift) ? '' : 'btn-icon-sm--green'" @click="openAssign(shift)" :title="isUnderstaffed(shift) ? 'Assign staff' : 'Reassign'">
                    <font-awesome-icon :icon="isUnderstaffed(shift) ? 'user-plus' : 'sync-alt'" />
                  </button>
                  <button class="btn-icon-sm btn-icon-sm--danger" @click="openDelete(shift)" title="Delete">
                    <font-awesome-icon icon="trash" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination bar -->
      <div class="pagination-bar" v-if="shiftPagination && shiftPagination.last_page > 1">
        <span class="pagination-info">
          Showing {{ (shiftPagination.current_page - 1) * shiftLength + 1 }}–{{ Math.min(shiftPagination.current_page * shiftLength, shiftPagination.total) }} of {{ shiftPagination.total }}
        </span>
        <div class="pagination-controls">
          <button class="pg-btn" :disabled="shiftPagination.current_page === 1" @click="goShiftPage(1)">«</button>
          <button class="pg-btn" :disabled="shiftPagination.current_page === 1" @click="goShiftPage(shiftPagination.current_page - 1)">‹</button>
          <div class="pg-pages">
            <button v-for="p in shiftPageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === shiftPagination.current_page }" @click="goShiftPage(p)">{{ p }}</button>
          </div>
          <button class="pg-btn" :disabled="shiftPagination.current_page === shiftPagination.last_page" @click="goShiftPage(shiftPagination.current_page + 1)">›</button>
          <button class="pg-btn" :disabled="shiftPagination.current_page === shiftPagination.last_page" @click="goShiftPage(shiftPagination.last_page)">»</button>
        </div>
      </div>

    </template>

    <!-- ══ MODAL: Create / Edit Shift ════════════════════════════════ -->
    <div class="modal-overlay" v-if="shiftModal.open" @click.self="shiftModal.open = false">
      <div class="modal-box">
        <div class="modal-head">
          <p class="modal-title">{{ shiftModal.id ? 'Edit Shift' : 'New Shift' }}</p>
          <button class="btn-icon-sm" @click="shiftModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">

          <div class="form-cols">
            <div class="form-row">
              <label class="form-lbl">Shift Type <span class="req">*</span></label>
              <select class="form-input" v-model="shiftModal.shift_type_id" @change="onShiftTypeChange">
                <option value="">— select type —</option>
                <option v-for="st in shiftTypes" :key="st.id" :value="st.id">{{ st.name }}</option>
              </select>
            </div>
            <div class="form-row">
              <label class="form-lbl">Shift Date <span class="hint">(blank = recurring)</span></label>
              <input type="date" class="form-input" v-model="shiftModal.shift_date" />
            </div>
          </div>

          <div class="form-cols">
            <div class="form-row">
              <label class="form-lbl">Staff Required <span class="req">*</span></label>
              <input type="number" class="form-input" v-model.number="shiftModal.required_employees" min="1" max="500" placeholder="e.g. 3" />
            </div>
            <div class="form-row">
              <label class="form-lbl"><font-awesome-icon icon="map-marker-alt" style="color:var(--ink-faint);margin-right:4px;" /> Location <span class="hint">(optional)</span></label>
              <select class="form-input" v-model="shiftModal.location_id" @change="onShiftLocationChange">
                <option :value="null">— none —</option>
                <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <label class="form-lbl"><font-awesome-icon icon="building" style="color:var(--ink-faint);margin-right:4px;" /> Client <span class="hint">(optional)</span></label>
            <search-select
              :key="`shift-client-${shiftModal.location_id || 'all'}`"
              v-model="shiftModal.client_id"
              url="/settings/customers/all"
              query-param="query"
              label-key="customer_name"
              sublabel-key="phone_no"
              value-key="id"
              response-key="data"
              placeholder="Search client…"
              search-placeholder="Type name…"
              :min-chars="1"
              :extra-params="shiftClientParams"
              :initial-label="shiftModal.client_label"
              @select="onShiftClientSelect"
            />
          </div>

          <div class="form-cols">
            <div class="form-row">
              <label class="form-lbl">Start Time <span class="req">*</span></label>
              <input type="time" class="form-input" v-model="shiftModal.start_time" />
            </div>
            <div class="form-row">
              <label class="form-lbl">End Time <span class="req">*</span></label>
              <input type="time" class="form-input" v-model="shiftModal.end_time" />
            </div>
          </div>

          <!-- Live duration preview -->
          <div v-if="shiftModal.start_time && shiftModal.end_time" class="duration-preview">
            <font-awesome-icon icon="clock" />
            Gross duration: <strong>{{ modalGrossDuration }}</strong>
            <template v-if="shiftModal.break_minutes > 0"> · Net after break: <strong>{{ modalNetDuration }}</strong></template>
          </div>

          <div class="form-cols">
            <div class="form-row">
              <label class="form-lbl">Break (minutes)</label>
              <input type="number" class="form-input" v-model.number="shiftModal.break_minutes" min="0" max="480" />
            </div>
            <div class="form-row">
              <label class="form-lbl">Timezone</label>
              <input type="text" class="form-input" v-model="shiftModal.timezone" placeholder="e.g. Europe/London" />
            </div>
          </div>

          <label class="check-label">
            <input type="checkbox" v-model="shiftModal.is_overnight" />
            <span>Overnight shift (crosses midnight)</span>
          </label>

        </div>
        <div class="modal-foot">
          <button class="btn-outline" @click="shiftModal.open = false">Cancel</button>
          <button class="btn-primary" :disabled="saving" @click="saveShift">
            <font-awesome-icon v-if="saving" icon="spinner" spin />
            {{ saving ? 'Saving…' : (shiftModal.id ? 'Update Shift' : 'Create Shift') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══ MODAL: Assign / Fill / Cover ══════════════════════════════ -->
    <div class="modal-overlay" v-if="assignModal.open" @click.self="assignModal.open = false">
      <div class="modal-box modal-box--assign">

        <!-- Head -->
        <div class="modal-head">
          <div>
            <p class="modal-title">Manage Staffing — {{ assignModal.shift?.shift_type?.name || assignModal.shift?.name }}</p>
            <p v-if="assignModal.shift?.client" class="modal-sub-client">
              <font-awesome-icon icon="building" /> {{ assignModal.shift.client.customer_name }}
            </p>
          </div>
          <button class="btn-icon-sm" @click="assignModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>

        <!-- Tabs -->
        <div class="assign-tabs">
          <button class="assign-tab" :class="{ 'assign-tab--active': assignModal.mode === 'fill' }" @click="assignModal.mode = 'fill'; assignModal.restWarnings = []">
            <font-awesome-icon icon="user-plus" /> Fill Vacancies
          </button>
          <button class="assign-tab" :class="{ 'assign-tab--active': assignModal.mode === 'cover' }" @click="assignModal.mode = 'cover'; assignModal.restWarnings = []">
            <font-awesome-icon icon="sync-alt" /> Cover / Replace
          </button>
          <button class="assign-tab" :class="{ 'assign-tab--active': assignModal.mode === 'full' }" @click="assignModal.mode = 'full'; assignModal.restWarnings = []">
            <font-awesome-icon icon="redo" /> Full Reassign
          </button>
        </div>

        <div class="modal-body">

          <!-- ── FILL VACANCIES ──────────────────────────────────────── -->
          <template v-if="assignModal.mode === 'fill'">
            <p class="assign-hint">Select a day with vacancies, then pick employees to fill the missing slots.</p>

            <!-- Day picker -->
            <div class="assign-days">
              <div
                v-for="day in assignModal.shift ? weekSchedule(assignModal.shift) : []"
                :key="day.iso"
                class="assign-day"
                :class="{
                  'assign-day--today':    day.isToday,
                  'assign-day--past':     day.isPast,
                  'assign-day--full':     !day.isPast && assignModal.shift && day.entries.length >= assignModal.shift.required_employees,
                  'assign-day--selected': assignModal.fillDay?.iso === day.iso,
                  'assign-day--vacant':   !day.isPast && assignModal.shift && day.entries.length < assignModal.shift.required_employees,
                }"
                :title="day.isPast ? 'Cannot edit past days' : ''"
                @click="!day.isPast && selectFillDay(day)"
              >
                <span class="aday-label">{{ day.label }}</span>
                <span class="aday-date">{{ day.dateNum }}</span>
                <span
                  class="aday-status"
                  :class="assignModal.shift && day.entries.length >= assignModal.shift.required_employees ? 'aday-status--full' : 'aday-status--short'"
                >
                  <template v-if="assignModal.shift && day.entries.length >= assignModal.shift.required_employees">✓</template>
                  <template v-else>−{{ assignModal.shift ? (assignModal.shift.required_employees - day.entries.length) : '?' }}</template>
                </span>
              </div>
            </div>

            <!-- Selected day: current staff + add employees -->
            <template v-if="assignModal.fillDay">
              <div class="assign-selected-day">
                <font-awesome-icon icon="calendar-day" />
                <strong>{{ assignModal.fillDay.label }} {{ assignModal.fillDay.dateNum }}</strong>
                <span v-if="assignModal.fillDay.entries.length < assignModal.shift.required_employees">
                  — needs <strong>{{ assignModal.shift.required_employees - assignModal.fillDay.entries.length }}</strong>
                  more employee{{ assignModal.shift.required_employees - assignModal.fillDay.entries.length !== 1 ? 's' : '' }}
                </span>
                <span v-else class="aday-status aday-status--full" style="margin-left:6px;">✓ Full</span>
              </div>

              <!-- Current staff on this day -->
              <div v-if="assignModal.fillDay.entries.length" class="current-staff-list">
                <p class="assign-section-lbl">Currently assigned</p>
                <div
                  v-for="entry in assignModal.fillDay.entries"
                  :key="entry.id"
                  class="current-staff-row"
                >
                  <span class="cover-avatar">{{ initials(entry.employee) }}</span>
                  <span class="current-staff-name">{{ empFullName(entry.employee) }}</span>
                  <span v-if="entry._source === 'rota'" class="current-staff-badge">Rota</span>
                  <button
                    v-if="entry._source === 'assignment'"
                    class="btn-icon-sm btn-icon-sm--danger"
                    :disabled="saving"
                    title="Remove from this day"
                    @click="removeAssignedEmployee(entry)"
                  >
                    <font-awesome-icon icon="user-minus" />
                  </button>
                  <span v-else class="current-staff-rota-lock" title="Managed via rota">
                    <font-awesome-icon icon="lock" />
                  </span>
                </div>
              </div>

              <!-- Add employees (only if not yet full) -->
              <template v-if="assignModal.fillDay.entries.length < assignModal.shift.required_employees">
                <p class="assign-section-lbl" style="margin-top:10px;">Add employees</p>
                <search-select
                  :key="assignModal.fillSearchKey"
                  v-model="assignModal.fillSearchId"
                  url="/payroll/employees/active"
                  query-param="search"
                  label-key="full_name"
                  sublabel-key="employee_no"
                  value-key="id"
                  response-key="data"
                  placeholder="Search employee…"
                  search-placeholder="Type name or number…"
                  :min-chars="1"
                  @select="onFillEmpSelect"
                />

                <div v-if="assignModal.fillEmployees.length" class="emp-chips">
                  <div v-for="e in assignModal.fillEmployees" :key="e.id" class="emp-chip">
                    <span class="chip-avatar">{{ (e.full_name || '?')[0].toUpperCase() }}</span>
                    <div class="chip-info">
                      <span class="chip-name">{{ e.full_name }}</span>
                      <span class="chip-no">{{ e.employee_no }}</span>
                    </div>
                    <button class="chip-remove" @click="assignModal.fillEmployees = assignModal.fillEmployees.filter(x => x.id !== e.id)">
                      <font-awesome-icon icon="times" />
                    </button>
                  </div>
                </div>
              </template>
            </template>
            <p v-else class="assign-hint assign-hint--muted">← Click a day above to manage staff.</p>
          </template>

          <!-- ── COVER / REPLACE ─────────────────────────────────────── -->
          <template v-else-if="assignModal.mode === 'cover'">
            <p class="assign-hint">Pick the day, select the employee to be covered, then choose their replacement. Rota employees will be fully transferred to the new employee.</p>

            <div class="assign-days">
              <div
                v-for="day in assignModal.shift ? weekSchedule(assignModal.shift) : []"
                :key="day.iso"
                class="assign-day"
                :class="{
                  'assign-day--today':    day.isToday,
                  'assign-day--past':     day.isPast,
                  'assign-day--selected': assignModal.coverDay?.iso === day.iso,
                  'assign-day--empty':    !day.entries.length,
                }"
                :title="day.isPast ? 'Cannot edit past days' : ''"
                @click="!day.isPast && day.entries.length && selectCoverDay(day)"
              >
                <span class="aday-label">{{ day.label }}</span>
                <span class="aday-date">{{ day.dateNum }}</span>
                <span class="aday-count">{{ day.entries.length }}</span>
              </div>
            </div>

            <template v-if="assignModal.coverDay">
              <p class="assign-section-lbl">Staff on {{ assignModal.coverDay.label }} {{ assignModal.coverDay.dateNum }}</p>
              <div class="cover-roster">
                <div
                  v-for="entry in assignModal.coverDay.entries"
                  :key="entry.id"
                  class="cover-emp"
                  :class="{ 'cover-emp--selected': assignModal.empToReplace?.id === entry.id }"
                  style="cursor:pointer"
                  @click="assignModal.empToReplace = entry; assignModal.coverEmployee = null; assignModal.coverSearchKey++"
                >
                  <span class="cover-avatar">{{ initials(entry.employee) }}</span>
                  <span class="cover-name">{{ empFullName(entry.employee) }}</span>
                  <!-- Rota entry: show badge or selected state; no remove button -->
                  <template v-if="entry._source === 'rota'">
                    <span class="cover-select-lbl" :class="{ 'cover-select-lbl--rota': assignModal.empToReplace?.id !== entry.id }">
                      {{ assignModal.empToReplace?.id === entry.id ? '✓ Selected' : 'Rota' }}
                    </span>
                  </template>
                  <!-- Assignment entry: select label + remove button -->
                  <template v-else>
                    <span class="cover-select-lbl">
                      {{ assignModal.empToReplace?.id === entry.id ? '✓ Selected' : 'Cover' }}
                    </span>
                    <button
                      class="btn-icon-sm btn-icon-sm--danger"
                      :disabled="saving"
                      title="Remove from this day"
                      @click.stop="removeCoverEmployee(entry)"
                    >
                      <font-awesome-icon icon="user-minus" />
                    </button>
                  </template>
                </div>
              </div>

              <template v-if="assignModal.empToReplace">
                <p class="assign-section-lbl">Cover employee for <strong>{{ empFullName(assignModal.empToReplace.employee) }}</strong>:</p>
                <search-select
                  :key="assignModal.coverSearchKey"
                  v-model="assignModal.coverSearchId"
                  url="/payroll/employees/active"
                  query-param="search"
                  label-key="full_name"
                  sublabel-key="employee_no"
                  value-key="id"
                  response-key="data"
                  placeholder="Search cover employee…"
                  search-placeholder="Type name or number…"
                  :min-chars="1"
                  @select="onCoverEmpSelect"
                />
                <div v-if="assignModal.coverEmployee" class="emp-chips">
                  <div class="emp-chip">
                    <span class="chip-avatar">{{ (assignModal.coverEmployee.full_name || '?')[0].toUpperCase() }}</span>
                    <div class="chip-info">
                      <span class="chip-name">{{ assignModal.coverEmployee.full_name }}</span>
                      <span class="chip-no">{{ assignModal.coverEmployee.employee_no }}</span>
                    </div>
                    <button class="chip-remove" @click="assignModal.coverEmployee = null"><font-awesome-icon icon="times" /></button>
                  </div>
                </div>
              </template>
            </template>
            <p v-else class="assign-hint assign-hint--muted">← Click a day that has employees scheduled.</p>
          </template>

          <!-- ── FULL REASSIGN ───────────────────────────────────────── -->
          <template v-else>
            <p class="assign-hint">Replace <strong>all</strong> current assignments for this shift with a new set of employees.</p>

            <!-- Current active assignments -->
            <template v-if="assignModal.shift?.assignments?.length">
              <p class="assign-section-lbl">Current assignments</p>
              <div class="current-staff-list">
                <div
                  v-for="a in assignModal.shift.assignments"
                  :key="a.id"
                  class="current-staff-row"
                >
                  <span class="cover-avatar">{{ initials(a.employee) }}</span>
                  <span class="current-staff-name">{{ empFullName(a.employee) }}</span>
                  <span class="current-staff-meta">{{ a.date_from?.slice(0,10) }}{{ a.date_to ? ' – ' + a.date_to.slice(0,10) : '' }}</span>
                  <button
                    class="btn-icon-sm btn-icon-sm--danger"
                    :disabled="saving"
                    title="Remove assignment"
                    @click="removeFullAssignment(a)"
                  >
                    <font-awesome-icon icon="user-minus" />
                  </button>
                </div>
              </div>
            </template>
            <p v-else class="assign-hint assign-hint--muted" style="margin-bottom:10px;">No active assignments yet.</p>

            <div class="form-cols">
              <div class="form-row">
                <label class="form-lbl">Effective From <span class="req">*</span></label>
                <input type="date" class="form-input" v-model="assignModal.date_from" />
              </div>
              <div class="form-row">
                <label class="form-lbl">Effective To <span class="hint">(open-ended if blank)</span></label>
                <input type="date" class="form-input" v-model="assignModal.date_to" />
              </div>
            </div>
            <div class="form-row">
              <label class="form-lbl">
                <font-awesome-icon icon="users" style="color:var(--accent);margin-right:4px;" />
                Employees
                <span class="emp-counter" :class="assignModal.fullEmployees.length === assignModal.requiredCount ? 'counter--ok' : 'counter--warn'">
                  {{ assignModal.fullEmployees.length }} / {{ assignModal.requiredCount }} selected
                </span>
              </label>
              <search-select
                v-if="assignModal.fullEmployees.length < assignModal.requiredCount"
                :key="assignModal.fullSearchKey"
                v-model="assignModal.fullSearchId"
                url="/payroll/employees/active"
                query-param="search"
                label-key="full_name"
                sublabel-key="employee_no"
                value-key="id"
                response-key="data"
                placeholder="Search employee…"
                search-placeholder="Type to search…"
                :min-chars="1"
                @select="onFullEmpSelect"
              />
              <p v-else class="field-hint counter--ok"><font-awesome-icon icon="check-circle" /> Shift fully staffed.</p>
              <div v-if="assignModal.fullEmployees.length" class="emp-chips">
                <div v-for="e in assignModal.fullEmployees" :key="e.id" class="emp-chip">
                  <span class="chip-avatar">{{ (e.full_name || '?')[0].toUpperCase() }}</span>
                  <div class="chip-info">
                    <span class="chip-name">{{ e.full_name }}</span>
                    <span class="chip-no">{{ e.employee_no }}</span>
                  </div>
                  <button class="chip-remove" @click="assignModal.fullEmployees = assignModal.fullEmployees.filter(x => x.id !== e.id)">
                    <font-awesome-icon icon="times" />
                  </button>
                </div>
              </div>
            </div>
          </template>

        </div>

        <!-- Footer -->
        <div class="modal-foot modal-foot--assign">

          <!-- Rest-period warnings -->
          <div v-if="assignModal.restWarnings.length" class="rest-warn-box">
            <p class="rest-warn-title"><font-awesome-icon icon="exclamation-triangle" /> Rest period warning</p>
            <ul class="rest-warn-list">
              <li v-for="(w, i) in assignModal.restWarnings" :key="i">{{ w }}</li>
            </ul>
            <p class="rest-warn-hint">This employee worked a 12h+ shift with insufficient rest. Proceed only if authorised.</p>
          </div>

          <!-- Remove confirmation strip -->
          <div v-if="assignModal.pendingRemove" class="remove-confirm-strip">
            <font-awesome-icon icon="exclamation-triangle" class="remove-confirm-icon" />
            <span class="remove-confirm-msg">Remove <strong>{{ assignModal.pendingRemove.name }}</strong> from this shift?</span>
            <div class="remove-confirm-btns">
              <button class="btn-xs btn-xs--outline" @click="cancelRemove">Keep</button>
              <button class="btn-xs btn-xs--danger" :disabled="saving" @click="confirmRemove">
                <font-awesome-icon v-if="saving" icon="spinner" spin />
                Remove
              </button>
            </div>
          </div>

          <label class="notify-check">
            <input type="checkbox" v-model="assignModal.notifyEmployee" />
            <span>Notify employee{{ assignModal.mode === 'full' && assignModal.fullEmployees.length > 1 ? 's' : '' }} by email</span>
          </label>

          <div class="modal-foot-btns">
            <button class="btn-outline" @click="assignModal.open = false; assignModal.restWarnings = []; assignModal.pendingRemove = null">Cancel</button>

            <template v-if="assignModal.mode === 'fill'">
              <button
                v-if="!assignModal.restWarnings.length"
                class="btn-primary"
                :disabled="saving || !assignModal.fillDay || !assignModal.fillEmployees.length"
                @click="submitFill(false)"
              >
                <font-awesome-icon v-if="saving" icon="spinner" spin />
                {{ saving ? 'Assigning…' : `Fill ${assignModal.fillDay?.label || 'Day'}` }}
              </button>
              <button v-else class="btn-warn" :disabled="saving" @click="submitFill(true)">
                <font-awesome-icon v-if="saving" icon="spinner" spin />
                {{ saving ? 'Assigning…' : 'Proceed Anyway' }}
              </button>
            </template>

            <template v-else-if="assignModal.mode === 'cover'">
              <button
                v-if="!assignModal.restWarnings.length"
                class="btn-primary"
                :disabled="saving || !assignModal.coverDay || !assignModal.empToReplace || !assignModal.coverEmployee"
                @click="submitCover(false)"
              >
                <font-awesome-icon v-if="saving" icon="spinner" spin />
                {{ saving ? 'Processing…' : (assignModal.empToReplace?._source === 'rota' ? 'Transfer to Cover' : 'Add Cover') }}
              </button>
              <button v-else class="btn-warn" :disabled="saving" @click="submitCover(true)">
                <font-awesome-icon v-if="saving" icon="spinner" spin />
                {{ saving ? 'Processing…' : 'Proceed Anyway' }}
              </button>
            </template>

            <template v-else>
              <button
                v-if="!assignModal.restWarnings.length"
                class="btn-primary"
                :disabled="saving || !assignModal.fullEmployees.length"
                @click="submitFullAssign(false)"
              >
                <font-awesome-icon v-if="saving" icon="spinner" spin />
                {{ saving ? 'Reassigning…' : 'Reassign All' }}
              </button>
              <button v-else class="btn-warn" :disabled="saving" @click="submitFullAssign(true)">
                <font-awesome-icon v-if="saving" icon="spinner" spin />
                {{ saving ? 'Reassigning…' : 'Proceed Anyway' }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ MODAL: Delete Confirm ══════════════════════════════════════ -->
    <div class="modal-overlay" v-if="deleteModal.show" @click.self="deleteModal.show = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-head">
          <p class="modal-title">Delete Shift</p>
          <button class="btn-icon-sm" @click="deleteModal.show = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p class="confirm-note">Delete <strong>{{ deleteModal.shift?.shift_type?.name || deleteModal.shift?.name }}</strong>? All assignments will also be removed. This cannot be undone.</p>
        </div>
        <div class="modal-foot">
          <button class="btn-outline" @click="deleteModal.show = false">Cancel</button>
          <button class="btn-danger" :disabled="saving" @click="submitDelete">
            <font-awesome-icon v-if="saving" icon="spinner" spin /> Delete
          </button>
        </div>
      </div>
    </div>

    <!-- ══ MODAL: Shift Detail ═══════════════════════════════════════ -->
    <div class="modal-overlay" v-if="detailModal.show" @click.self="detailModal.show = false">
      <div class="modal-box modal-box--detail">

        <!-- Header -->
        <div class="detail-head">
          <div class="detail-head-icon">
            <font-awesome-icon icon="clock" />
          </div>
          <div class="detail-head-info">
            <p class="detail-shift-name">{{ detailModal.shift?.shift_type?.name || detailModal.shift?.name }}</p>
            <p v-if="detailModal.shift?.location" class="detail-client">
              <font-awesome-icon icon="map-marker-alt" />
              {{ detailModal.shift.location.name }}
            </p>
            <p v-if="detailModal.shift?.client" class="detail-client">
              <font-awesome-icon icon="building" />
              {{ detailModal.shift.client.customer_name }}
            </p>
          </div>
          <div class="detail-head-badges">
            <span class="badge" :class="detailModal.shift?.is_active ? 'badge--green' : 'badge--gray'">
              {{ detailModal.shift?.is_active ? 'Active' : 'Inactive' }}
            </span>
            <span v-if="detailModal.shift?.is_overnight" class="badge badge--night">Overnight</span>
            <span v-if="detailModal.shift && isUnderstaffed(detailModal.shift)" class="badge badge--warn">⚠ Understaffed</span>
          </div>
          <button class="detail-close" @click="detailModal.show = false"><font-awesome-icon icon="times" /></button>
        </div>

        <div class="detail-body">

          <!-- Shift Info -->
          <div class="detail-section">
            <p class="detail-section-title"><font-awesome-icon icon="info-circle" /> Shift Details</p>
            <div class="detail-info-grid">
              <div class="detail-info-item">
                <span class="di-lbl">Start</span>
                <span class="di-val">{{ fmtShiftTime(detailModal.shift?.start_time) }}</span>
              </div>
              <div class="detail-info-item">
                <span class="di-lbl">End</span>
                <span class="di-val">{{ fmtShiftTime(detailModal.shift?.end_time) }}</span>
              </div>
              <div class="detail-info-item">
                <span class="di-lbl">Net Duration</span>
                <span class="di-val di-val--accent">{{ detailModal.shift ? shiftDuration(detailModal.shift) : '—' }}</span>
              </div>
              <div class="detail-info-item">
                <span class="di-lbl">Break</span>
                <span class="di-val">{{ detailModal.shift?.break_minutes }}m</span>
              </div>
              <div class="detail-info-item">
                <span class="di-lbl">Staff Required</span>
                <span class="di-val">{{ detailModal.shift?.required_employees }}</span>
              </div>
              <div v-if="detailModal.shift?.location" class="detail-info-item">
                <span class="di-lbl">Location</span>
                <span class="di-val">{{ detailModal.shift.location.name }}</span>
              </div>
              <div v-if="detailModal.shift?.timezone && detailModal.shift.timezone !== 'UTC'" class="detail-info-item">
                <span class="di-lbl">Timezone</span>
                <span class="di-val">{{ detailModal.shift.timezone }}</span>
              </div>
            </div>
          </div>

          <!-- Weekly Rota -->
          <div class="detail-section">
            <p class="detail-section-title"><font-awesome-icon icon="calendar-alt" /> This Week's Rota <span class="detail-week-range">{{ weekLabel }}</span></p>
            <div class="detail-rota-grid">
              <div
                v-for="day in detailModal.shift ? weekSchedule(detailModal.shift) : []"
                :key="day.iso"
                class="detail-rota-row"
                :class="{
                  'detail-rota-row--today': day.isToday,
                  'detail-rota-row--empty': !day.entries.length,
                }"
              >
                <div class="detail-rota-day">
                  <span class="drota-day" :class="day.isToday ? 'drota-day--today' : ''">{{ day.label }}</span>
                  <span class="drota-date">{{ day.dateNum }}</span>
                </div>
                <div v-if="day.entries.length" class="detail-rota-emps">
                  <div v-for="entry in day.entries" :key="entry.id" class="detail-rota-emp">
                    <span class="drota-avatar">{{ initials(entry.employee) }}</span>
                    <span class="drota-name">{{ empFullName(entry.employee) }}</span>
                    <span class="drota-time">
                      {{ fmtShiftTime(entry.start_time || detailModal.shift.start_time) }} – {{ fmtShiftTime(entry.end_time || detailModal.shift.end_time) }}
                    </span>
                    <button
                      v-if="entry._source === 'assignment'"
                      class="btn-icon-sm btn-icon-sm--danger drota-remove"
                      :disabled="detailModal.saving"
                      title="Remove from this day"
                      @click.stop="removeDetailEmployee(entry)"
                    >
                      <font-awesome-icon icon="user-minus" />
                    </button>
                    <span v-else-if="entry._source === 'rota'" class="current-staff-badge" style="margin-left:auto">Rota</span>
                  </div>
                </div>
                <span v-else class="drota-none">No coverage</span>
                <span
                  v-if="detailModal.shift?.required_employees"
                  class="week-staff-badge"
                  :class="day.entries.length >= detailModal.shift.required_employees ? 'week-staff-badge--ok' : 'week-staff-badge--short'"
                >
                  <template v-if="day.entries.length >= detailModal.shift.required_employees">✓ Full</template>
                  <template v-else>−{{ detailModal.shift.required_employees - day.entries.length }} needed</template>
                </span>
              </div>
            </div>
          </div>

        </div>

        <div class="detail-foot" :class="{ 'detail-foot--col': detailModal.pendingRemove }">

          <!-- Remove confirmation strip -->
          <div v-if="detailModal.pendingRemove" class="remove-confirm-strip" style="align-self:stretch">
            <font-awesome-icon icon="exclamation-triangle" class="remove-confirm-icon" />
            <span class="remove-confirm-msg">Remove <strong>{{ detailModal.pendingRemove.name }}</strong>?</span>
            <label class="notify-check" style="margin-left:auto;margin-right:8px;white-space:nowrap">
              <input type="checkbox" v-model="detailModal.notifyEmployee" />
              <span>Notify</span>
            </label>
            <div class="remove-confirm-btns">
              <button class="btn-xs btn-xs--outline" @click="detailModal.pendingRemove = null">Keep</button>
              <button class="btn-xs btn-xs--danger" :disabled="detailModal.saving" @click="confirmDetailRemove">
                <font-awesome-icon v-if="detailModal.saving" icon="spinner" spin />
                Remove
              </button>
            </div>
          </div>

          <template v-if="!detailModal.pendingRemove">
            <button class="btn-outline" @click="detailModal.show = false; detailModal.pendingRemove = null">Close</button>
            <button class="btn-primary" @click="openEditShift(detailModal.shift); detailModal.show = false">
              <font-awesome-icon icon="pen" /> Edit Shift
            </button>
            <button
              class="btn-primary"
              :style="{ background: isUnderstaffed(detailModal.shift) ? '' : 'var(--accent-dark)' }"
              @click="openAssign(detailModal.shift); detailModal.show = false"
            >
              <font-awesome-icon :icon="isUnderstaffed(detailModal.shift) ? 'user-plus' : 'sync-alt'" />
              {{ isUnderstaffed(detailModal.shift) ? 'Assign Staff' : 'Reassign' }}
            </button>
          </template>
        </div>
      </div>
    </div>

    <!-- ══ MODAL: Unassign Confirm ════════════════════════════════════ -->
    <div class="modal-overlay" v-if="unassignModal.show" @click.self="unassignModal.show = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-head">
          <p class="modal-title">Unassign Employee</p>
          <button class="btn-icon-sm" @click="unassignModal.show = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p class="confirm-note">
            Remove <strong>{{ empFullName(unassignModal.assignment?.employee) }}</strong> from this shift assignment?
          </p>
        </div>
        <div class="modal-foot">
          <button class="btn-outline" @click="unassignModal.show = false">Cancel</button>
          <button class="btn-danger" :disabled="saving" @click="submitUnassign">
            <font-awesome-icon v-if="saving" icon="spinner" spin /> Unassign
          </button>
        </div>
      </div>
    </div>

    <!-- ══ MODAL: Shift Types Management ════════════════════════════ -->
    <div class="modal-overlay" v-if="shiftTypesModal.open" @click.self="shiftTypesModal.open = false">
      <div class="modal-box modal-box--types">
        <div class="modal-head">
          <p class="modal-title">Manage Shift Types</p>
          <button class="btn-icon-sm" @click="shiftTypesModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <!-- Add form -->
          <div class="type-add-row">
            <input type="text" class="form-input" v-model="shiftTypesModal.newName" placeholder="Type name…" style="flex:1" />
            <input type="text" class="form-input" v-model="shiftTypesModal.newDesc" placeholder="Description (optional)" style="flex:2" />
            <input type="color" class="form-input form-input--color" v-model="shiftTypesModal.newColor" title="Colour" />
            <button class="btn-primary" :disabled="shiftTypesModal.saving || !shiftTypesModal.newName.trim()" @click="saveShiftType">
              <font-awesome-icon v-if="shiftTypesModal.saving" icon="spinner" spin />
              <font-awesome-icon v-else icon="plus" />
              Add
            </button>
          </div>
          <!-- List -->
          <div v-if="shiftTypes.length" class="type-list">
            <div v-for="st in shiftTypes" :key="st.id" class="type-row">
              <template v-if="shiftTypesModal.editingId !== st.id">
                <span class="type-dot" :style="{ background: st.color }"></span>
                <span class="type-name">{{ st.name }}</span>
                <span class="type-desc">{{ st.description }}</span>
                <div class="type-actions">
                  <button class="btn-icon-sm" @click="startEditType(st)" title="Edit"><font-awesome-icon icon="pen" /></button>
                  <button class="btn-icon-sm btn-icon-sm--danger" @click="deleteShiftType(st.id)" title="Delete"><font-awesome-icon icon="trash" /></button>
                </div>
              </template>
              <template v-else>
                <input type="color" class="form-input form-input--color" v-model="shiftTypesModal.editColor" />
                <input type="text" class="form-input" v-model="shiftTypesModal.editName" style="flex:1" />
                <input type="text" class="form-input" v-model="shiftTypesModal.editDesc" style="flex:2" placeholder="Description" />
                <div class="type-actions">
                  <button class="btn-primary" :disabled="shiftTypesModal.saving" @click="updateShiftType(st.id)">
                    <font-awesome-icon v-if="shiftTypesModal.saving" icon="spinner" spin /> Save
                  </button>
                  <button class="btn-outline" @click="shiftTypesModal.editingId = null">Cancel</button>
                </div>
              </template>
            </div>
          </div>
          <p v-else class="type-empty">No shift types yet. Add one above.</p>
        </div>
        <div class="modal-foot">
          <button class="btn-outline" @click="shiftTypesModal.open = false">Close</button>
        </div>
      </div>
    </div>

    <!-- ══ Column picker modal ══ -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-outline" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary" :disabled="selectedExportCols.length === 0 || exportLoading" @click="doExport()">
              <font-awesome-icon :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exportLoading" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import SearchSelect from '../../../../components/SearchSelect.vue';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default {
  name: 'ShiftsPage',
  components: { SearchSelect },

  data() {
    return {
      toast: useToast(),
      loading:        false,
      saving:         false,
      shifts:         [],
      shiftLength:    5,
      shiftPage:      1,
      shiftPagination: null,
      exportOpen:         false,
      showExportModal:    false,
      pendingExportFmt:   'csv',
      selectedExportCols: [],
      exportLoading:      false,
      exportColumnDefs: [
        { key: 'name',       label: 'Shift Name', pdfWidth: 120 },
        { key: 'shift_type', label: 'Type',        pdfWidth: 90 },
        { key: 'client',     label: 'Client',      pdfWidth: 110 },
        { key: 'location',   label: 'Location',    pdfWidth: 90 },
        { key: 'start_time', label: 'Start',       pdfWidth: 70 },
        { key: 'end_time',   label: 'End',         pdfWidth: 70 },
        { key: 'break',      label: 'Break',       pdfWidth: 65 },
        { key: 'duration',   label: 'Duration',    pdfWidth: 75 },
        { key: 'status',     label: 'Status',      pdfWidth: 65 },
      ],

      viewMode:          'card',
      filterSearch:      '',
      filterStatus:      'all',
      filterStaff:       'all',
      filterShiftType:   '',
      filterLocation:    '',
      filterClientId:    null,
      filterClientLabel: '',
      filterClientKey:   0,

      shiftTypes: [],

      shiftTypesModal: {
        open: false, saving: false,
        newName: '', newDesc: '', newColor: '#1F6F5C',
        editingId: null, editName: '', editDesc: '', editColor: '#1F6F5C',
      },

      statusOpts: [
        { value: 'all',      label: 'All statuses' },
        { value: 'active',   label: 'Active'        },
        { value: 'inactive', label: 'Inactive'      },
      ],
      staffOpts: [
        { value: 'all',        label: 'All staffing'  },
        { value: 'complete',   label: 'Fully staffed' },
        { value: 'incomplete', label: 'Understaffed'  },
      ],

      locations: [],

      shiftModal: {
        open: false, id: null, name: '', shift_type_id: null, shift_date: '', required_employees: 1,
        start_time: '09:00', end_time: '17:00', break_minutes: 60,
        timezone: 'UTC', is_overnight: false,
        client_id: null, client_label: '', clientObj: null,
        location_id: null,
      },

      assignModal: {
        open: false, mode: 'fill', shift: null,
        notifyEmployee: true,
        restWarnings: [],
        pendingRemove: null, // { id, name, type: 'fill'|'cover'|'full' }
        // Fill mode
        fillDay: null, fillEmployees: [], fillSearchId: null, fillSearchKey: 0,
        // Cover mode
        coverDay: null, empToReplace: null, coverEmployee: null, coverSearchId: null, coverSearchKey: 0,
        // Full reassign mode
        requiredCount: 1, date_from: '', date_to: '',
        fullEmployees: [], fullSearchId: null, fullSearchKey: 0,
      },

      deleteModal:   { show: false, shift: null },
      unassignModal: { show: false, assignment: null },
      detailModal:   { show: false, shift: null, pendingRemove: null, saving: false, notifyEmployee: true },

      searchTimer: null,
    };
  },

  watch: {
    filterSearch()    { this.shiftPage = 1; this.debouncedFetchShifts(); },
    filterStatus()    { this.shiftPage = 1; this.fetchShifts(); },
    filterStaff()     { this.shiftPage = 1; this.fetchShifts(); },
    filterShiftType() { this.shiftPage = 1; this.fetchShifts(); },
    filterLocation()  { this.shiftPage = 1; this.fetchShifts(); },
    filterClientId()  { this.shiftPage = 1; this.fetchShifts(); },
  },

  computed: {
    shiftPageRange() {
      if (!this.shiftPagination) return [];
      const cur = this.shiftPagination.current_page, last = this.shiftPagination.last_page;
      const pages = [];
      for (let p = Math.max(1, cur - 2); p <= Math.min(last, cur + 2); p++) pages.push(p);
      return pages;
    },

    today() {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    },

    weekDates() {
      const d = new Date();
      const dow = d.getDay();
      const monday = new Date(d);
      monday.setDate(d.getDate() - ((dow + 6) % 7));
      monday.setHours(0, 0, 0, 0);
      return Array.from({ length: 7 }, (_, i) => {
        const day = new Date(monday);
        day.setDate(monday.getDate() + i);
        return `${day.getFullYear()}-${String(day.getMonth()+1).padStart(2,'0')}-${String(day.getDate()).padStart(2,'0')}`;
      });
    },

    weekLabel() {
      const dates = this.weekDates;
      const fmt = iso => {
        const [, m, d] = iso.split('-');
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${parseInt(d)} ${months[parseInt(m)-1]}`;
      };
      return `${fmt(dates[0])} – ${fmt(dates[6])}`;
    },

    filteredShifts() {
      return this.shifts;
    },

    modalGrossDuration() {
      const m = this.durationMin(this.shiftModal.start_time, this.shiftModal.end_time, this.shiftModal.is_overnight);
      return this.fmtMins(m);
    },

    modalNetDuration() {
      const gross = this.durationMin(this.shiftModal.start_time, this.shiftModal.end_time, this.shiftModal.is_overnight);
      return this.fmtMins(Math.max(0, gross - (this.shiftModal.break_minutes || 0)));
    },

    shiftClientParams() {
      const p = { is_client: 1 };
      if (this.shiftModal.location_id) p.location_id = this.shiftModal.location_id;
      return p;
    },
  },

  mounted() {
    this.fetchShifts();
    this.fetchShiftTypes();
    this.fetchLocations();
  },

  methods: {
    // ── Time helpers ─────────────────────────────────────────────────
    timeToMin(t) {
      if (!t) return 0;
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    },

    durationMin(start, end, isOvernight) {
      if (!start || !end) return 0;
      let d = this.timeToMin(end) - this.timeToMin(start);
      if (isOvernight || d <= 0) d += 1440;
      return Math.max(0, d);
    },

    fmtMins(m) {
      const h = Math.floor(m / 60);
      const mn = m % 60;
      return mn ? `${h}h ${mn}m` : `${h}h`;
    },

    shiftDuration(shift) {
      const gross = this.durationMin(shift.start_time, shift.end_time, shift.is_overnight);
      const net   = Math.max(0, gross - (shift.break_minutes || 0));
      return this.fmtMins(net);
    },

    fmtShiftTime(t) {
      if (!t) return '—';
      const [h, m] = t.split(':').map(Number);
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12  = h % 12 || 12;
      return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
    },

    weekSchedule(shift) {
      const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const rota        = shift.rota_entries || [];
      const assignments = (shift.assignments || []).filter(a => a.is_active !== false);

      return this.weekDates.map((iso, i) => {
        // Rota entries whose work_date is exactly this day
        const rotaEntries  = rota.filter(e => e.work_date?.slice(0, 10) === iso)
          .map(e => ({ ...e, _source: 'rota' }));
        const rotaEmpIds   = new Set(rotaEntries.map(e => e.employee_id));

        // Shift assignments whose date range covers this day, not already in rota
        const assignEntries = assignments
          .filter(a => {
            const from = a.date_from?.slice(0, 10);
            const to   = a.date_to?.slice(0, 10);
            return from && from <= iso && (!to || to >= iso) && !rotaEmpIds.has(a.employee_id);
          })
          .map(a => ({ ...a, _source: 'assignment' }));

        const entries  = [...rotaEntries, ...assignEntries];
        const dateNum  = parseInt(iso.split('-')[2]);
        return {
          iso,
          label: dayLabels[i],
          dateNum,
          isToday: iso === this.today,
          isPast:  iso < this.today,
          entries,
        };
      });
    },

    // ── Staffing helpers ─────────────────────────────────────────────
    isUnderstaffed(shift) {
      if (!shift.required_employees) return false;
      return (shift.assignments?.length || 0) < shift.required_employees;
    },

    staffPct(shift) {
      if (!shift.required_employees) return 100;
      return Math.min(100, ((shift.assignments?.length || 0) / shift.required_employees) * 100);
    },

    // ── Formatting ───────────────────────────────────────────────────
    fmtDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    empFullName(emp) {
      if (!emp) return 'Unknown';
      return emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Unknown';
    },

    initials(emp) {
      if (!emp) return '?';
      return ((emp.first_name?.[0] || '') + (emp.last_name?.[0] || '')).toUpperCase() || '?';
    },

    // ── Data ─────────────────────────────────────────────────────────
    async fetchShifts() {
      this.loading = true;
      try {
        const params = { length: this.shiftLength, page: this.shiftPage };
        if (this.filterSearch.trim())              params.search        = this.filterSearch.trim();
        if (this.filterStatus !== 'all')           params.is_active     = this.filterStatus === 'active' ? 1 : 0;
        if (this.filterStaff  !== 'all')           params.staff         = this.filterStaff;
        if (this.filterShiftType)                  params.shift_type_id = this.filterShiftType;
        if (this.filterLocation)                   params.location_id   = this.filterLocation;
        if (this.filterClientId)                   params.client_id     = this.filterClientId;
        const { data } = await axios.get('/hr/shift', { params });
        this.shiftPagination = data.data;
        this.shifts = data.data?.data || [];
      } catch {
        this.toast.error('Failed to load shifts');
      } finally {
        this.loading = false;
      }
    },

    goShiftPage(p) { this.shiftPage = p; this.fetchShifts(); },

    debouncedFetchShifts() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.fetchShifts(), 300);
    },

    resetFilters() {
      this.filterSearch      = '';
      this.filterStatus      = 'all';
      this.filterStaff       = 'all';
      this.filterShiftType   = '';
      this.filterLocation    = '';
      this.filterClientId    = null;
      this.filterClientLabel = '';
      this.filterClientKey++;
      this.shiftPage    = 1;
      this.shiftLength  = 5;
      this.fetchShifts();
    },

    onFilterClientSelect(opt) {
      this.filterClientId    = opt?.id    ?? null;
      this.filterClientLabel = opt?.customer_name ?? '';
    },
    clearClientFilter() {
      this.filterClientId    = null;
      this.filterClientLabel = '';
      this.filterClientKey++;
    },

    // ── Shift Types ───────────────────────────────────────────────────────────
    async fetchShiftTypes() {
      try {
        const { data } = await axios.get('/hr/shift-type/active');
        this.shiftTypes = data.data || [];
      } catch {/* silent */}
    },

    async fetchLocations() {
      try {
        const { data } = await axios.get('/admin/location/active');
        this.locations = data.data || [];
      } catch {/* silent */}
    },

    onShiftTypeChange() {
      const type = this.shiftTypes.find(t => t.id === this.shiftModal.shift_type_id);
      if (type) this.shiftModal.name = type.name;
    },

    openShiftTypes() {
      this.shiftTypesModal.open = true;
      this.fetchShiftTypes();
    },

    async saveShiftType() {
      if (!this.shiftTypesModal.newName.trim()) return;
      this.shiftTypesModal.saving = true;
      try {
        await axios.post('/hr/shift-type', {
          name: this.shiftTypesModal.newName.trim(),
          description: this.shiftTypesModal.newDesc.trim() || null,
          color: this.shiftTypesModal.newColor,
        });
        this.shiftTypesModal.newName = '';
        this.shiftTypesModal.newDesc = '';
        this.shiftTypesModal.newColor = '#1F6F5C';
        this.toast.success('Shift type added');
        await this.fetchShiftTypes();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to add type');
      } finally {
        this.shiftTypesModal.saving = false;
      }
    },

    startEditType(st) {
      this.shiftTypesModal.editingId = st.id;
      this.shiftTypesModal.editName  = st.name;
      this.shiftTypesModal.editDesc  = st.description || '';
      this.shiftTypesModal.editColor = st.color || '#1F6F5C';
    },

    async updateShiftType(id) {
      this.shiftTypesModal.saving = true;
      try {
        await axios.put(`/hr/shift-type/${id}`, {
          name: this.shiftTypesModal.editName,
          description: this.shiftTypesModal.editDesc || null,
          color: this.shiftTypesModal.editColor,
          is_active: true,
        });
        this.shiftTypesModal.editingId = null;
        this.toast.success('Updated');
        await this.fetchShiftTypes();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Update failed');
      } finally {
        this.shiftTypesModal.saving = false;
      }
    },

    async deleteShiftType(id) {
      if (!confirm('Delete this shift type?')) return;
      try {
        await axios.delete(`/hr/shift-type/${id}`);
        this.toast.success('Deleted');
        await this.fetchShiftTypes();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Delete failed');
      }
    },

    // ── Create / Edit ─────────────────────────────────────────────────
    openCreateShift() {
      this.shiftModal = {
        open: true, id: null, name: '', shift_type_id: null, shift_date: '', required_employees: 1,
        start_time: '09:00', end_time: '17:00', break_minutes: 60,
        timezone: 'UTC', is_overnight: false,
        client_id: null, client_label: '', clientObj: null,
        location_id: null,
      };
    },

    openEditShift(shift) {
      this.shiftModal = {
        open: true, id: shift.id,
        name: shift.name,
        shift_type_id:      shift.shift_type_id ?? null,
        shift_date:         shift.shift_date ? String(shift.shift_date).slice(0, 10) : '',
        required_employees: shift.required_employees || 1,
        start_time:         shift.start_time,
        end_time:           shift.end_time,
        break_minutes:      shift.break_minutes,
        timezone:           shift.timezone,
        is_overnight:       shift.is_overnight,
        client_id:    shift.client_id    ?? null,
        client_label: shift.client?.customer_name ?? '',
        clientObj:    shift.client       ?? null,
        location_id:  shift.location_id  ?? null,
      };
    },

    onShiftLocationChange() {
      this.shiftModal.client_id    = null;
      this.shiftModal.client_label = '';
      this.shiftModal.clientObj    = null;
    },

    onShiftClientSelect(opt) {
      this.shiftModal.clientObj    = opt;
      this.shiftModal.client_label = opt?.customer_name ?? '';
      this.shiftModal.client_id    = opt?.id ?? null;
    },

    async saveShift() {
      const m = this.shiftModal;
      if (!m.shift_type_id || !m.start_time || !m.end_time) {
        this.toast.error('Shift type, start time and end time are required'); return;
      }
      if (!m.required_employees || m.required_employees < 1) {
        this.toast.error('Staff required must be at least 1'); return;
      }
      if (m.shift_type_id && m.client_id) {
        const duplicate = this.shifts.find(s =>
          s.id !== m.id &&
          s.shift_type_id === m.shift_type_id &&
          s.client_id === m.client_id
        );
        if (duplicate) {
          this.toast.error('A shift with this type already exists for this client'); return;
        }
      }
      this.saving = true;
      try {
        const payload = {
          ...m,
          start_time: m.start_time.slice(0, 5),
          end_time:   m.end_time.slice(0, 5),
        };
        if (m.id) {
          await axios.put(`/hr/shift/${m.id}`, payload);
        } else {
          await axios.post('/hr/shift', payload);
        }
        this.toast.success(m.id ? 'Shift updated' : 'Shift created');
        this.shiftModal.open = false;
        this.fetchShifts();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Save failed');
      } finally {
        this.saving = false;
      }
    },

    async toggleStatus(shift) {
      try {
        await axios.put(`/hr/shift/${shift.id}/status`);
        shift.is_active = !shift.is_active;
        this.toast.success('Status updated');
      } catch {
        this.toast.error('Failed to update status');
      }
    },

    // ── Detail ───────────────────────────────────────────────────────
    openDetail(shift) { this.detailModal = { show: true, shift, pendingRemove: null, saving: false, notifyEmployee: true }; },

    removeDetailEmployee(entry) {
      this.detailModal.pendingRemove = { id: entry.id, name: this.empFullName(entry.employee) };
    },

    async confirmDetailRemove() {
      const pr = this.detailModal.pendingRemove;
      if (!pr) return;
      this.detailModal.pendingRemove = null;
      this.detailModal.saving = true;
      try {
        await axios.delete(`/hr/shift/assignment/${pr.id}`, {
          data: { notify: this.detailModal.notifyEmployee },
        });
        this.toast.success(`${pr.name} removed`);
        await this.fetchShifts();
        // Re-point detailModal.shift to the fresh object
        const fresh = this.shifts.find(s => s.id === this.detailModal.shift?.id);
        if (fresh) this.detailModal.shift = fresh;
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to remove employee');
      } finally {
        this.detailModal.saving = false;
      }
    },

    // ── Delete ────────────────────────────────────────────────────────
    openDelete(shift) { this.deleteModal = { show: true, shift }; },
    async submitDelete() {
      this.saving = true;
      try {
        await axios.delete(`/hr/shift/${this.deleteModal.shift.id}`);
        this.toast.success('Shift deleted');
        this.deleteModal.show = false;
        this.fetchShifts();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Delete failed');
      } finally {
        this.saving = false;
      }
    },

    // ── Assign modal ──────────────────────────────────────────────────
    refreshAssignModalShift() {
      if (!this.assignModal.open || !this.assignModal.shift) return;
      const updated = this.shifts.find(s => s.id === this.assignModal.shift.id);
      if (updated) this.assignModal.shift = updated;
    },

    openAssign(shift) {
      this.assignModal = {
        open: true, mode: 'fill', shift,
        notifyEmployee: true,
        restWarnings: [],
        fillDay: null, fillEmployees: [], fillSearchId: null, fillSearchKey: 0,
        coverDay: null, empToReplace: null, coverEmployee: null, coverSearchId: null, coverSearchKey: 0,
        requiredCount: shift.required_employees || 1,
        date_from: this.today, date_to: '',
        fullEmployees: [], fullSearchId: null, fullSearchKey: 0,
      };
    },

    selectFillDay(day) {
      this.assignModal.fillDay       = day;
      this.assignModal.fillEmployees = [];
      this.assignModal.fillSearchKey++;
    },

    selectCoverDay(day) {
      this.assignModal.coverDay       = day;
      this.assignModal.empToReplace   = null;
      this.assignModal.coverEmployee  = null;
      this.assignModal.coverSearchKey++;
    },

    onFillEmpSelect(opt) {
      if (!opt) return;
      if (this.assignModal.fillEmployees.find(e => e.id === opt.id)) { this.toast.error('Already added'); return; }
      const needed = this.assignModal.shift.required_employees - this.assignModal.fillDay.entries.length;
      if (this.assignModal.fillEmployees.length >= needed) { this.toast.error(`Only ${needed} more needed`); return; }
      this.assignModal.fillEmployees.push({ id: opt.id, full_name: opt.full_name, employee_no: opt.employee_no });
      this.assignModal.fillSearchId  = null;
      this.assignModal.fillSearchKey++;
    },

    onCoverEmpSelect(opt) {
      if (!opt) return;
      this.assignModal.coverEmployee = { id: opt.id, full_name: opt.full_name, employee_no: opt.employee_no };
      this.assignModal.coverSearchId = null;
    },

    onFullEmpSelect(opt) {
      if (!opt) return;
      if (this.assignModal.fullEmployees.find(e => e.id === opt.id)) { this.toast.error('Already added'); return; }
      if (this.assignModal.fullEmployees.length >= this.assignModal.requiredCount) return;
      this.assignModal.fullEmployees.push({ id: opt.id, full_name: opt.full_name, employee_no: opt.employee_no });
      this.assignModal.fullSearchId  = null;
      this.assignModal.fullSearchKey++;
    },

    async _doRemoveAssignment(assignmentId, empName, afterRemove) {
      this.saving = true;
      try {
        await axios.delete(`/hr/shift/assignment/${assignmentId}`, {
          data: { notify: this.assignModal.notifyEmployee },
        });
        this.toast.success(`${empName} removed`);
        await this.fetchShifts();
        this.refreshAssignModalShift();
        if (afterRemove) afterRemove();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to remove employee');
      } finally {
        this.saving = false;
      }
    },

    removeAssignedEmployee(entry) {
      this.assignModal.pendingRemove = { id: entry.id, name: this.empFullName(entry.employee), type: 'fill', entry };
    },

    removeCoverEmployee(entry) {
      this.assignModal.pendingRemove = { id: entry.id, name: this.empFullName(entry.employee), type: 'cover', entry };
    },

    removeFullAssignment(assignment) {
      this.assignModal.pendingRemove = { id: assignment.id, name: this.empFullName(assignment.employee), type: 'full', entry: assignment };
    },

    cancelRemove() {
      this.assignModal.pendingRemove = null;
    },

    async confirmRemove() {
      const pr = this.assignModal.pendingRemove;
      if (!pr) return;
      this.assignModal.pendingRemove = null;

      if (pr.type === 'fill') {
        await this._doRemoveAssignment(pr.id, pr.name, () => {
          if (this.assignModal.fillDay) {
            const freshDay = this.weekSchedule(this.assignModal.shift)
              .find(d => d.iso === this.assignModal.fillDay.iso);
            if (freshDay) this.assignModal.fillDay = freshDay;
          }
        });
      } else if (pr.type === 'cover') {
        await this._doRemoveAssignment(pr.id, pr.name, () => {
          if (this.assignModal.coverDay) {
            const freshDay = this.weekSchedule(this.assignModal.shift)
              .find(d => d.iso === this.assignModal.coverDay.iso);
            if (freshDay) {
              this.assignModal.coverDay = freshDay;
              if (this.assignModal.empToReplace?.id === pr.entry.id) {
                this.assignModal.empToReplace  = null;
                this.assignModal.coverEmployee = null;
              }
            }
          }
        });
      } else {
        await this._doRemoveAssignment(pr.id, pr.name, null);
      }
    },

    async submitFill(force = false) {
      if (!this.assignModal.fillDay || !this.assignModal.fillEmployees.length) return;
      if (this.assignModal.fillDay.isPast) {
        this.toast.error('Cannot add staff to a past day');
        return;
      }
      this.saving = true;
      try {
        const { data } = await axios.post('/hr/shift/fill-day', {
          shift_id:     this.assignModal.shift.id,
          date:         this.assignModal.fillDay.iso,
          employee_ids: this.assignModal.fillEmployees.map(e => e.id),
          notify:       this.assignModal.notifyEmployee,
          force,
        });
        if (data.data?.requires_confirmation) {
          this.assignModal.restWarnings = data.data.warnings;
          return;
        }
        this.assignModal.restWarnings = [];
        this.toast.success(`${this.assignModal.fillDay.label} filled`);
        this.assignModal.fillDay = null;
        this.assignModal.fillEmployees = [];
        this.assignModal.fillSearchKey++;
        await this.fetchShifts();
        this.refreshAssignModalShift();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to fill day');
      } finally {
        this.saving = false;
      }
    },

    async submitCover(force = false) {
      const { shift, coverDay, empToReplace, coverEmployee } = this.assignModal;
      if (!coverDay || !empToReplace || !coverEmployee) return;
      this.saving = true;
      try {
        if (empToReplace._source === 'rota') {
          // Transfer the rota entry from the original employee to the cover employee
          await axios.put(`/hr/rota/entries/${empToReplace.id}/replace`, {
            new_employee_id: coverEmployee.id,
          });
          this.toast.success(`${this.empFullName(empToReplace.employee)} transferred to ${coverEmployee.full_name}`);
        } else {
          // Existing: add a fill-day assignment for the cover employee
          if (coverDay.isPast) {
            this.toast.error('Cannot add cover on a past day');
            return;
          }
          const { data } = await axios.post('/hr/shift/fill-day', {
            shift_id:     shift.id,
            date:         coverDay.iso,
            employee_ids: [coverEmployee.id],
            notify:       this.assignModal.notifyEmployee,
            force,
          });
          if (data.data?.requires_confirmation) {
            this.assignModal.restWarnings = data.data.warnings;
            return;
          }
          this.assignModal.restWarnings = [];
          this.toast.success(`Cover added for ${this.empFullName(empToReplace.employee)} on ${coverDay.label}`);
        }
        this.assignModal.coverDay      = null;
        this.assignModal.empToReplace  = null;
        this.assignModal.coverEmployee = null;
        this.assignModal.coverSearchKey++;
        await this.fetchShifts();
        this.refreshAssignModalShift();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to add cover');
      } finally {
        this.saving = false;
      }
    },

    async submitFullAssign(force = false) {
      if (!this.assignModal.date_from || !this.assignModal.fullEmployees.length) {
        this.toast.error('Effective date and at least one employee are required'); return;
      }
      this.saving = true;
      try {
        const { data } = await axios.post('/hr/shift/assign', {
          shift_id:     this.assignModal.shift.id,
          employee_ids: this.assignModal.fullEmployees.map(e => e.id),
          date_from:    this.assignModal.date_from,
          date_to:      this.assignModal.date_to || null,
          notify:       this.assignModal.notifyEmployee,
          force,
        });
        if (data.data?.requires_confirmation) {
          this.assignModal.restWarnings = data.data.warnings;
          return;
        }
        this.assignModal.restWarnings = [];
        this.assignModal.open = false;
        this.toast.success('Shift reassigned');
        this.fetchShifts();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Reassign failed');
      } finally {
        this.saving = false;
      }
    },

    // ── Unassign ──────────────────────────────────────────────────────
    confirmUnassign(assignment) {
      this.unassignModal = { show: true, assignment };
    },

    async submitUnassign() {
      const dateFrom = this.unassignModal.assignment?.date_from?.slice(0, 10);
      if (dateFrom && dateFrom < this.today) {
        this.toast.error('Cannot remove a shift assignment that has already passed');
        this.unassignModal.show = false;
        return;
      }
      this.saving = true;
      try {
        await axios.delete(`/hr/shift/assignment/${this.unassignModal.assignment.id}`);
        this.toast.success('Employee unassigned');
        this.unassignModal.show = false;
        await this.fetchShifts();
        this.refreshAssignModalShift();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Unassign failed');
      } finally {
        this.saving = false;
      }
    },

    openExportModal(fmt) {
      this.pendingExportFmt   = fmt;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
    },
    getShiftVal(shift, key) {
      switch (key) {
        case 'name':       return shift.name || '—';
        case 'shift_type': return shift.shift_type?.name || '—';
        case 'client':     return shift.client?.customer_name || '—';
        case 'location':   return shift.location?.name || '—';
        case 'start_time': return shift.start_time || '—';
        case 'end_time':   return shift.end_time || '—';
        case 'break':      return shift.break_minutes ? shift.break_minutes + ' min' : '—';
        case 'duration': {
          if (!shift.start_time || !shift.end_time) return '—';
          const [sh, sm] = shift.start_time.split(':').map(Number);
          const [eh, em] = shift.end_time.split(':').map(Number);
          let mins = (eh * 60 + em) - (sh * 60 + sm);
          if (mins < 0) mins += 24 * 60;
          mins -= (shift.break_minutes || 0);
          const h = Math.floor(mins / 60), m = mins % 60;
          return m ? `${h}h ${m}m` : `${h}h`;
        }
        case 'status': return shift.is_active ? 'Active' : 'Inactive';
        default: return '—';
      }
    },
    doExport() {
      const cols = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
      const rows = this.filteredShifts;
      if (this.pendingExportFmt === 'csv') {
        const header = cols.map(c => `"${c.label}"`).join(',');
        const body   = rows.map(r => cols.map(c => `"${String(this.getShiftVal(r, c.key)).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob(['﻿' + header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'shifts.csv'; a.click(); URL.revokeObjectURL(url);
      } else {
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const usable = 761;
        const totalW = cols.reduce((s, c) => s + c.pdfWidth, 0);
        const scale  = usable / totalW;
        const colStyles = {};
        cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
        doc.autoTable({
          head: [cols.map(c => c.label)],
          body: rows.map(r => cols.map(c => this.getShiftVal(r, c.key))),
          startY: 40, margin: { left: 20, right: 20 }, tableWidth: usable,
          styles: { fontSize: 8, cellPadding: 4, overflow: 'ellipsize' },
          headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold' },
          columnStyles: colStyles,
        });
        doc.save('shifts.pdf');
      }
      this.showExportModal = false;
    },
  },
};
</script>

<style scoped>
/* ── Design tokens ─────────────────────────────────────────── */
.shifts-page {
  --paper:       #F4F6F5;
  --surface:     #FFFFFF;
  --ink:         #16241F;
  --ink-muted:   #56685F;
  --ink-faint:   #8B978F;
  --line:        #DCE3DF;
  --accent:      #1F6F5C;
  --accent-soft: #E3F0EC;
  --accent-dark: #0F4136;
  --alert:       #B4432A;
  --alert-soft:  #F7E7E1;
  --amber:       #9C6C10;
  --amber-soft:  #F6EDD9;
  --radius:      10px;
  display: flex; flex-direction: column;
  font-family: 'Inter', sans-serif; color: var(--ink);
}
.mono { font-family: 'IBM Plex Mono', 'Courier New', monospace; }

/* ── Page header ─────────────────────────────────────────────── */
.page-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:18px; flex-wrap:wrap; gap:12px; }
.page-title  { font-size:20px; font-weight:700; color:var(--ink); margin:0 0 3px; }
.page-sub    { font-size:12px; color:var(--ink-muted); margin:0; }

/* ── Buttons ─────────────────────────────────────────────────── */
button { font-family:'Inter',sans-serif; cursor:pointer; }

.btn-primary {
  display:inline-flex; align-items:center; gap:6px;
  padding:8px 14px; background:var(--accent); color:#fff;
  border:1px solid var(--accent); border-radius:8px;
  font-size:13px; font-weight:500; transition:background .12s;
}
.btn-primary:hover    { background:var(--accent-dark); border-color:var(--accent-dark); }
.btn-primary:disabled { opacity:.6; cursor:not-allowed; }

.btn-outline {
  display:inline-flex; align-items:center; gap:6px;
  padding:8px 14px; background:var(--surface); color:var(--ink);
  border:1px solid var(--line); border-radius:8px;
  font-size:13px; font-weight:500; transition:all .12s;
}
.btn-outline:hover { border-color:var(--accent); background:var(--accent-soft); }

.btn-icon-sm {
  width:30px; height:30px; background:none; border:1px solid var(--line);
  border-radius:7px; color:var(--ink-muted); font-size:13px;
  display:flex; align-items:center; justify-content:center; transition:all .12s;
}
.btn-icon-sm:hover           { border-color:var(--accent); background:var(--accent-soft); color:var(--accent); }
.btn-icon-sm--danger:hover   { border-color:var(--alert); background:var(--alert-soft); color:var(--alert); }
.btn-icon-sm--green          { color:var(--accent); }
.btn-icon-sm--green:hover    { border-color:var(--accent); background:var(--accent-soft); }

.btn-assign {
  display:flex; align-items:center; justify-content:center; gap:6px;
  padding:7px 13px; background:var(--accent-soft); color:var(--accent);
  border:1px solid var(--line); border-radius:8px;
  font-size:12px; font-weight:500; transition:background .12s; width:100%;
}
.btn-assign:hover    { background:#d0e8e2; }
.btn-assign--full    { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
.btn-assign--full:hover { background:#dcfce7; }

.btn-danger {
  display:inline-flex; align-items:center; gap:6px;
  padding:8px 14px; background:var(--alert); color:#fff;
  border:1px solid var(--alert); border-radius:8px;
  font-size:13px; font-weight:500;
}
.btn-danger:hover    { opacity:.9; }
.btn-danger:disabled { opacity:.6; cursor:not-allowed; }

/* ── Filter bar ──────────────────────────────────────────────── */
.filter-bar {
  display:flex; align-items:center; flex-wrap:wrap; gap:10px;
  background:var(--surface); border:1px solid var(--line); border-radius:var(--radius);
  padding:12px 16px; margin-bottom:18px;
}
.search-wrap  { display:flex; align-items:center; gap:8px; background:var(--paper); border:1px solid var(--line); border-radius:8px; padding:0 10px; height:36px; flex:1; min-width:160px; }
.search-ico   { color:var(--ink-faint); font-size:12px; flex-shrink:0; }
.search-input { border:none; background:none; outline:none; font-size:13px; color:var(--ink); width:100%; }
.search-input::placeholder { color:var(--ink-faint); }
.filter-sel   { height:36px; padding:0 10px; border:1px solid var(--line); border-radius:8px; background:var(--surface); font-size:13px; color:var(--ink); outline:none; }
.filter-client-wrap { display:flex; align-items:center; gap:4px; min-width:180px; max-width:220px; }
.filter-clear { background:none; border:none; color:var(--ink-faint); font-size:12px; padding:4px; border-radius:4px; cursor:pointer; flex-shrink:0; }
.filter-clear:hover { color:var(--alert); }
.filter-count { font-size:12px; color:var(--ink-faint); font-weight:600; white-space:nowrap; margin-left:auto; }
.view-toggle  { display:flex; gap:2px; border:1px solid var(--line); border-radius:8px; overflow:hidden; }
.view-btn     { width:34px; height:34px; background:var(--surface); border:none; cursor:pointer; color:var(--ink-muted); font-size:13px; display:flex; align-items:center; justify-content:center; transition:all .15s; }
.view-btn:hover     { background:var(--paper); color:var(--accent); }
.view-btn--active   { background:var(--accent); color:#fff; }

/* ── Cards ────────────────────────────────────────────────────── */
.shifts-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:14px; }
.shift-card  { background:var(--surface); border:1px solid var(--line); border-radius:var(--radius); padding:16px; display:flex; flex-direction:column; gap:12px; transition:box-shadow .15s; }
.shift-card:hover { box-shadow:0 4px 16px rgba(0,0,0,.07); }

.card-head       { display:flex; align-items:center; gap:10px; }
.card-icon       { width:40px; height:40px; border-radius:9px; background:var(--accent-soft); color:var(--accent); display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
.card-name-block { flex:1; min-width:0; }
.card-name       { font-size:14px; font-weight:700; color:var(--ink); margin:0 0 2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.card-client     { display:flex; align-items:center; gap:5px; font-size:12px; font-weight:700; color:var(--accent-dark); margin:0 0 5px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.card-client-icon{ font-size:10px; color:var(--accent); flex-shrink:0; }
.card-badges     { display:flex; flex-wrap:wrap; gap:3px; }
.card-actions    { display:flex; gap:3px; }

/* ── Badges ──────────────────────────────────────────────────── */
.badge       { display:inline-block; font-size:10px; font-weight:700; padding:2px 8px; border-radius:20px; }
.badge--green { background:#dcfce7; color:#16a34a; }
.badge--gray  { background:var(--paper); color:var(--ink-muted); border:1px solid var(--line); }
.badge--warn  { background:var(--amber-soft); color:var(--amber); border:1px solid #e0c87a; }
.badge--night { background:#ede9fe; color:#6d28d9; }

/* ── Shift times ─────────────────────────────────────────────── */
.shift-times { display:flex; align-items:center; gap:12px; background:var(--paper); border-radius:8px; padding:10px 12px; flex-wrap:wrap; }
.time-block  { display:flex; flex-direction:column; gap:2px; }
.time-lbl    { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--ink-faint); }
.time-val    { font-size:13px; font-weight:700; color:var(--ink); }
.time-val--accent { color:var(--accent); }
.time-val--tz { font-size:11px; font-weight:600; color:var(--ink-muted); }
.time-sep    { color:var(--ink-faint); font-size:13px; }
.time-block--dur { margin-left:auto; }

/* ── Weekly rota schedule ────────────────────────────────────── */
.week-schedule  { display:flex; flex-direction:column; gap:6px; }
.week-header    { display:flex; align-items:center; justify-content:space-between; }
.week-title     { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--ink-faint); }
.week-range     { font-size:10px; color:var(--ink-faint); }
.week-grid      { display:flex; flex-direction:column; gap:2px; }
.week-row       { display:flex; align-items:center; gap:8px; padding:5px 8px; border-radius:7px; background:var(--paper); min-height:32px; }
.week-row--today{ background:#e8f4ee; }
.week-row--empty{ opacity:.6; }
.week-day       { font-size:11px; font-weight:700; color:var(--ink-muted); width:26px; flex-shrink:0; }
.week-day--today{ color:var(--accent); }
.week-date-num  { font-size:10px; color:var(--ink-faint); width:16px; flex-shrink:0; }
.week-none      { font-size:12px; color:var(--ink-faint); margin-left:2px; }
.week-emps      { display:flex; align-items:center; gap:4px; flex:1; flex-wrap:wrap; }
.week-time-tag  { font-size:10px; font-weight:600; color:var(--accent); background:var(--accent-soft); padding:2px 7px; border-radius:20px; white-space:nowrap; margin-left:auto; flex-shrink:0; }
.week-staff-badge       { font-size:10px; font-weight:700; padding:2px 6px; border-radius:20px; flex-shrink:0; white-space:nowrap; }
.week-staff-badge--ok   { background:#dcfce7; color:#16a34a; }
.week-staff-badge--short{ background:var(--amber-soft); color:var(--amber); }

/* ── Week employee avatars ───────────────────────────────────── */
.week-emp-wrap    { position:relative; }
.week-emp-wrap:hover { z-index:20; }
.week-emp-avatar  { width:22px; height:22px; border-radius:50%; background:var(--accent); color:#fff; font-size:9px; font-weight:700; display:flex; align-items:center; justify-content:center; border:2px solid var(--surface); cursor:default; }
.week-emp-tooltip {
  display:none; position:absolute; bottom:calc(100% + 8px); left:50%;
  transform:translateX(-50%); background:var(--ink); color:#fff;
  border-radius:9px; padding:9px 12px; min-width:160px;
  z-index:500; pointer-events:none; white-space:nowrap;
  box-shadow:0 6px 20px rgba(0,0,0,.25);
}
.week-emp-tooltip::after {
  content:''; position:absolute; top:100%; left:50%;
  transform:translateX(-50%); border:6px solid transparent;
  border-top-color:var(--ink);
}
.week-emp-wrap:hover .week-emp-tooltip { display:block; }

/* ── Avatars (table view) ────────────────────────────────────── */
.emp-avatars     { display:flex; align-items:center; flex-wrap:wrap; gap:0; }
.emp-avatar-wrap { position:relative; margin-right:-5px; }
.emp-avatar-wrap:hover { z-index:20; }
.emp-avatar      { width:28px; height:28px; border-radius:50%; background:var(--accent); color:#fff; font-size:10px; font-weight:700; display:flex; align-items:center; justify-content:center; border:2px solid var(--surface); cursor:default; }
.emp-avatar--sm  { width:24px; height:24px; font-size:9px; }
.emp-avatar--more{ background:var(--paper); color:var(--ink-muted); border-color:var(--line); }

/* ── Tooltip ─────────────────────────────────────────────────── */
.emp-tooltip {
  display:none; position:absolute; bottom:calc(100% + 10px); left:50%;
  transform:translateX(-50%); background:var(--ink); color:#fff;
  border-radius:9px; padding:10px 13px; min-width:170px; max-width:220px;
  z-index:500; pointer-events:none; white-space:nowrap;
  box-shadow:0 6px 20px rgba(0,0,0,.25);
}
.emp-tooltip--up { bottom:auto; top:calc(100% + 10px); }
.emp-tooltip::after {
  content:''; position:absolute; top:100%; left:50%;
  transform:translateX(-50%); border:6px solid transparent;
  border-top-color:var(--ink);
}
.emp-tooltip--up::after { top:auto; bottom:100%; border-top-color:transparent; border-bottom-color:var(--ink); }
.emp-avatar-wrap:hover .emp-tooltip { display:block; pointer-events:auto; }
.et-name     { font-size:12px; font-weight:700; margin:0 0 5px; color:#fff; white-space:normal; }
.et-row      { font-size:11px; margin:0 0 2px; color:rgba(255,255,255,.75); display:flex; gap:6px; }
.et-lbl      { color:rgba(255,255,255,.4); font-weight:600; min-width:32px; flex-shrink:0; }
.et-unassign {
  display:block; width:100%; margin-top:7px;
  padding:4px 0; background:rgba(180,67,42,.85); color:#fff; border:none;
  border-radius:5px; font-size:11px; font-weight:600; cursor:pointer; text-align:center;
  pointer-events:auto;
}
.et-unassign:hover { background:var(--alert); }

/* ── Table ───────────────────────────────────────────────────── */
.table-wrap { overflow-x:auto; border:1px solid var(--line); border-radius:var(--radius); background:var(--surface); }
.shifts-table { width:100%; border-collapse:collapse; font-size:13px; }
.shifts-table thead th { padding:10px 13px; text-align:left; font-size:10px; font-weight:700; color:var(--ink-muted); text-transform:uppercase; letter-spacing:.05em; border-bottom:1px solid var(--line); background:var(--paper); white-space:nowrap; }
.tbl-row td { padding:11px 13px; border-bottom:1px solid var(--line); vertical-align:middle; }
.tbl-row:last-child td { border-bottom:none; }
.tbl-row:hover td { background:var(--paper); }
.th-actions   { text-align:right; }
.tbl-name     { font-size:13px; font-weight:700; color:var(--ink); margin:0 0 2px; }
.tbl-muted    { color:var(--ink-muted); font-size:12px; }
.tbl-time     { white-space:nowrap; font-size:13px; font-weight:600; }
.tbl-dur      { color:var(--accent); font-weight:700; font-size:12px; }
.tbl-client   { font-size:12px; color:var(--ink-muted); }
.tbl-staff    { display:flex; align-items:center; gap:8px; }
.tbl-actions  { display:flex; gap:3px; justify-content:flex-end; }
.tbl-empty    { text-align:center; padding:40px 20px; color:var(--ink-muted); font-size:13px; }
.tbl-link     { background:none; border:none; cursor:pointer; color:var(--accent); font-size:13px; text-decoration:underline; padding:0; }

/* ── Empty ───────────────────────────────────────────────────── */
.empty-state { grid-column:1/-1; display:flex; flex-direction:column; align-items:center; gap:14px; padding:60px 20px; color:var(--ink-muted); }
.empty-icon  { font-size:48px; opacity:.25; }
.empty-state p { font-size:14px; margin:0; }

/* ── Skeleton ────────────────────────────────────────────────── */
.sk-card { height:220px; border-radius:var(--radius); background:linear-gradient(90deg,var(--paper) 25%,var(--line) 50%,var(--paper) 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
@keyframes shimmer { 0%{background-position:200% 0}100%{background-position:-200% 0} }

/* ── Modals ──────────────────────────────────────────────────── */
.modal-overlay   { position:fixed; inset:0; background:rgba(0,0,0,.4); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box       { background:var(--surface); border-radius:var(--radius); width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--sm   { max-width:380px; }
.modal-box--assign { min-height:520px; }
.modal-head      { display:flex; align-items:center; justify-content:space-between; padding:15px 20px; border-bottom:1px solid var(--line); background:var(--paper); border-radius:var(--radius) var(--radius) 0 0; }
.modal-title     { font-size:14px; font-weight:700; color:var(--ink); margin:0; }
.modal-body      { padding:18px 20px; display:flex; flex-direction:column; gap:14px; }
.modal-body--search { padding-bottom:260px; }
.modal-foot      { display:flex; justify-content:flex-end; gap:8px; padding:13px 20px; border-top:1px solid var(--line); }
.modal-foot button { padding:7px 14px; border-radius:8px; font-size:13px; font-weight:500; border:1px solid var(--line); background:var(--surface); color:var(--ink); }
.modal-foot button:hover { border-color:var(--accent); background:var(--accent-soft); }

/* ── Form ────────────────────────────────────────────────────── */
.form-cols  { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.form-row   { display:flex; flex-direction:column; gap:5px; }
.form-lbl   { font-size:11px; font-weight:600; color:var(--ink-muted); text-transform:uppercase; letter-spacing:.04em; }
.form-input { width:100%; padding:8px 10px; border:1px solid var(--line); border-radius:8px; font-size:13px; color:var(--ink); background:var(--surface); font-family:'Inter',sans-serif; outline:none; box-sizing:border-box; }
.form-input:focus { border-color:var(--accent); }
.hint { font-size:10.5px; font-weight:400; color:var(--ink-faint); text-transform:none; margin-left:3px; letter-spacing:0; }
.req  { color:var(--alert); }
.check-label { display:flex; align-items:center; gap:8px; font-size:13px; color:var(--ink); cursor:pointer; }
.field-hint  { font-size:11px; color:var(--ink-muted); margin:3px 0 0; }

/* Duration preview */
.duration-preview { font-size:12px; color:var(--ink-muted); background:var(--accent-soft); border-radius:7px; padding:7px 11px; }
.duration-preview strong { color:var(--accent-dark); }

/* ── Assign modal chips ──────────────────────────────────────── */
.modal-sub-client { font-size:12px; color:var(--accent-dark); font-weight:600; margin:3px 0 0; display:flex; align-items:center; gap:5px; }
.emp-counter   { margin-left:8px; font-size:11px; font-weight:700; padding:2px 8px; border-radius:20px; }
.counter--ok   { background:#dcfce7; color:#16a34a; }
.counter--warn { background:var(--amber-soft); color:var(--amber); }

.emp-chips  { display:flex; flex-direction:column; gap:6px; margin-top:8px; }
.emp-chip   { display:flex; align-items:center; gap:10px; padding:8px 10px; background:var(--paper); border:1px solid var(--line); border-radius:8px; }
.chip-avatar{ width:30px; height:30px; border-radius:50%; background:var(--accent); color:#fff; font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.chip-info  { flex:1; min-width:0; display:flex; flex-direction:column; gap:1px; }
.chip-name  { font-size:13px; font-weight:600; color:var(--ink); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.chip-no    { font-size:11px; color:var(--ink-muted); }
.chip-remove{ background:none; border:none; cursor:pointer; color:var(--ink-faint); font-size:12px; padding:4px; border-radius:4px; flex-shrink:0; }
.chip-remove:hover { color:var(--alert); background:var(--alert-soft); }

/* ── Assign tabs ─────────────────────────────────────────────── */
.assign-tabs    { display:flex; border-bottom:1px solid var(--line); background:var(--paper); }
.assign-tab     { flex:1; padding:10px 8px; border:none; background:none; font-size:12px; font-weight:600; color:var(--ink-muted); cursor:pointer; display:flex; align-items:center; justify-content:center; gap:6px; border-bottom:2px solid transparent; transition:all .12s; }
.assign-tab:hover      { color:var(--ink); background:#f0f0ee; }
.assign-tab--active    { color:var(--accent); border-bottom-color:var(--accent); background:#fff; }

/* ── Assign day grid ─────────────────────────────────────────── */
.assign-hint           { font-size:12px; color:var(--ink-muted); margin:0; line-height:1.5; }
.assign-hint--muted    { color:var(--ink-faint); font-style:italic; }
.assign-days           { display:grid; grid-template-columns:repeat(7,1fr); gap:4px; }
.assign-day            { display:flex; flex-direction:column; align-items:center; gap:3px; padding:8px 4px; border:1px solid var(--line); border-radius:8px; cursor:pointer; transition:all .12s; background:var(--surface); }
.assign-day:hover:not(.assign-day--full):not(.assign-day--past) { border-color:var(--accent); background:var(--accent-soft); }
.assign-day--full      { opacity:.5; cursor:default; }
.assign-day--empty     { opacity:.45; cursor:default; }
.assign-day--past      { opacity:.4; cursor:default; filter:grayscale(.5); background:#f7f7f7; }
.assign-day--today .aday-label { color:var(--accent); font-weight:800; }
.assign-day--selected  { border-color:var(--accent); background:var(--accent-soft); box-shadow:0 0 0 2px rgba(31,111,92,.15); }
.assign-day--vacant    { border-color:var(--amber); }
.aday-label            { font-size:11px; font-weight:700; color:var(--ink-muted); }
.aday-date             { font-size:12px; font-weight:700; color:var(--ink); }
.aday-status           { font-size:10px; font-weight:700; padding:1px 5px; border-radius:20px; }
.aday-status--full     { background:#dcfce7; color:#16a34a; }
.aday-status--short    { background:var(--amber-soft); color:var(--amber); }
.aday-count            { font-size:12px; font-weight:700; color:var(--accent); }

.assign-selected-day   { display:flex; align-items:center; gap:7px; font-size:13px; color:var(--ink-muted); background:var(--paper); border-radius:8px; padding:8px 12px; }
.assign-section-lbl    { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:var(--ink-faint); margin:4px 0 6px; }

/* ── Cover roster ────────────────────────────────────────────── */
.cover-roster  { display:flex; flex-direction:column; gap:5px; margin-bottom:4px; }
.cover-emp     { display:flex; align-items:center; gap:10px; padding:9px 12px; border:1px solid var(--line); border-radius:8px; cursor:pointer; background:var(--paper); transition:all .12s; }
.cover-emp:hover { border-color:var(--accent); background:var(--accent-soft); }
.cover-emp--selected { border-color:var(--accent); background:var(--accent-soft); }
.cover-avatar  { width:28px; height:28px; border-radius:50%; background:var(--accent); color:#fff; font-size:10px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.cover-name    { flex:1; font-size:13px; font-weight:600; color:var(--ink); }
.cover-select-lbl           { font-size:11px; font-weight:600; color:var(--accent); }
.cover-select-lbl--rota     { color:var(--ink-muted); background:var(--accent-soft); padding:2px 7px; border-radius:20px; font-size:10px; }

/* ── Confirm notes ───────────────────────────────────────────── */
.confirm-note { font-size:13px; color:var(--ink-muted); background:var(--paper); border-radius:8px; padding:10px 13px; margin:0; line-height:1.6; }

/* ── Detail modal ────────────────────────────────────────────── */
.modal-box--detail { max-width:620px; max-height:90vh; display:flex; flex-direction:column; overflow:hidden; }

.detail-head        { display:flex; align-items:flex-start; gap:12px; padding:18px 20px; border-bottom:1px solid var(--line); background:var(--paper); flex-shrink:0; flex-wrap:wrap; }
.detail-head-icon   { width:44px; height:44px; border-radius:11px; background:var(--accent-soft); color:var(--accent); display:flex; align-items:center; justify-content:center; font-size:20px; flex-shrink:0; }
.detail-head-info   { flex:1; min-width:0; }
.detail-shift-name  { font-size:16px; font-weight:700; color:var(--ink); margin:0 0 3px; }
.detail-client      { display:flex; align-items:center; gap:6px; font-size:13px; font-weight:700; color:var(--accent-dark); margin:0; }
.detail-head-badges { display:flex; flex-wrap:wrap; gap:4px; align-self:center; }
.detail-close       { background:none; border:none; cursor:pointer; color:var(--ink-faint); font-size:16px; padding:4px; border-radius:6px; align-self:flex-start; }
.detail-close:hover { color:var(--ink); background:var(--line); }

.detail-body         { flex:1; overflow-y:auto; display:flex; flex-direction:column; }
.detail-section      { padding:16px 20px; border-bottom:1px solid var(--line); }
.detail-section:last-child { border-bottom:none; }
.detail-section-title{ font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--ink-faint); margin:0 0 12px; display:flex; align-items:center; gap:6px; }
.detail-week-range   { font-weight:400; text-transform:none; letter-spacing:0; color:var(--ink-muted); margin-left:4px; }

.detail-info-grid    { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.detail-info-item    { display:flex; flex-direction:column; gap:3px; background:var(--paper); border-radius:8px; padding:9px 12px; }
.di-lbl              { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--ink-faint); }
.di-val              { font-size:14px; font-weight:700; color:var(--ink); }
.di-val--accent      { color:var(--accent); }

.detail-rota-grid    { display:flex; flex-direction:column; gap:4px; }
.detail-rota-row     { display:flex; align-items:center; gap:10px; padding:8px 10px; border-radius:8px; background:var(--paper); flex-wrap:wrap; }
.detail-rota-row--today { background:#e8f4ee; }
.detail-rota-row--empty { opacity:.6; }
.detail-rota-day     { display:flex; flex-direction:column; align-items:center; width:32px; flex-shrink:0; }
.drota-day           { font-size:11px; font-weight:700; color:var(--ink-muted); }
.drota-day--today    { color:var(--accent); }
.drota-date          { font-size:10px; color:var(--ink-faint); }
.detail-rota-emps    { display:flex; flex-direction:column; gap:4px; flex:1; }
.detail-rota-emp     { display:flex; align-items:center; gap:7px; }
.drota-avatar        { width:24px; height:24px; border-radius:50%; background:var(--accent); color:#fff; font-size:9px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.drota-name          { font-size:12px; font-weight:600; color:var(--ink); flex:1; }
.drota-time          { font-size:11px; color:var(--accent); font-weight:600; background:var(--accent-soft); padding:2px 7px; border-radius:20px; white-space:nowrap; }
.drota-none          { font-size:12px; color:var(--ink-faint); flex:1; }

.detail-foot         { display:flex; justify-content:flex-end; gap:8px; padding:13px 20px; border-top:1px solid var(--line); flex-shrink:0; }
.detail-foot--col    { flex-direction:column; align-items:stretch; }
.drota-remove        { margin-left:auto; flex-shrink:0; }

/* ── Table rota dots ─────────────────────────────────────────── */
.tbl-row--click      { cursor:pointer; }
.tbl-client-name     { display:flex; align-items:center; gap:4px; font-size:11px; font-weight:700; color:var(--accent-dark); margin:2px 0 0; }
.tbl-client-icon     { font-size:9px; color:var(--accent); flex-shrink:0; }
.tbl-rota-week       { display:flex; gap:4px; align-items:flex-end; }
.tbl-rota-day        { display:flex; flex-direction:column; align-items:center; gap:2px; }
.tbl-rota-day--today .tbl-rota-lbl { color:var(--accent); font-weight:700; }
.tbl-rota-lbl        { font-size:9px; color:var(--ink-faint); font-weight:600; text-transform:uppercase; }
.tbl-rota-dot        { width:10px; height:10px; border-radius:50%; }
.tbl-rota-dot--ok      { background:var(--accent); }
.tbl-rota-dot--partial { background:var(--amber); }
.tbl-rota-dot--none    { background:var(--line); border:1px solid var(--ink-faint); }

/* ── Shift Types ─────────────────────────────────────────────── */
.card-name  { display:flex; align-items:center; gap:6px; }
.type-dot   { display:inline-block; width:10px; height:10px; border-radius:50%; flex-shrink:0; }
.type-add-row { display:flex; gap:8px; align-items:center; margin-bottom:14px; }
.type-list  { display:flex; flex-direction:column; gap:6px; max-height:340px; overflow-y:auto; }
.type-row   { display:flex; align-items:center; gap:8px; padding:8px 10px; background:var(--paper); border-radius:8px; }
.type-name  { font-weight:600; font-size:13px; flex:1; }
.type-desc  { font-size:12px; color:var(--ink-muted); flex:2; }
.type-actions { display:flex; gap:4px; margin-left:auto; }
.type-empty { text-align:center; color:var(--ink-muted); padding:20px; font-size:13px; }
.form-input--color { width:40px; height:36px; padding:2px; cursor:pointer; border-radius:6px; }
.modal-box--types  { max-width:680px; }

/* ── Assign modal footer ─────────────────────────────────────── */
.modal-foot--assign   { flex-direction:column; align-items:stretch; gap:10px; }
.modal-foot-btns      { display:flex; gap:8px; justify-content:flex-end; }
.notify-check         { display:flex; align-items:center; gap:8px; font-size:13px; color:var(--ink-muted); cursor:pointer; padding:6px 2px; }
.notify-check input   { width:15px; height:15px; accent-color:var(--accent); cursor:pointer; }

/* ── Current staff list (fill day) ──────────────────────────── */
.current-staff-list      { margin-bottom:8px; }
.current-staff-row       { display:flex; align-items:center; gap:8px; padding:7px 10px; background:var(--paper); border-radius:8px; border:1px solid var(--line); margin-bottom:5px; }
.current-staff-name      { flex:1; font-size:13px; font-weight:600; color:var(--ink); }
.current-staff-meta      { font-size:11px; color:var(--ink-faint); white-space:nowrap; }
.current-staff-badge     { font-size:10px; font-weight:700; color:var(--accent); background:var(--accent-soft); padding:2px 7px; border-radius:20px; }
.current-staff-rota-lock { font-size:11px; color:var(--ink-faint); }

/* ── Rest-period warning box ─────────────────────────────────── */
.rest-warn-box        { background:#fffbeb; border:1px solid #fcd34d; border-radius:8px; padding:10px 13px; }
.rest-warn-title      { font-size:12px; font-weight:700; color:#92400e; margin:0 0 6px; display:flex; align-items:center; gap:6px; }
.rest-warn-list       { margin:0 0 6px; padding-left:18px; }
.rest-warn-list li    { font-size:12px; color:#78350f; line-height:1.6; }
.rest-warn-hint       { font-size:11px; color:#a16207; margin:0; font-style:italic; }
.btn-warn             { display:inline-flex; align-items:center; gap:6px; padding:8px 18px; border-radius:8px; font-size:13px; font-weight:600; background:#d97706; color:#fff; border:none; cursor:pointer; transition:background .15s; }
.btn-warn:hover       { background:#b45309; }
.btn-warn:disabled    { opacity:.6; cursor:not-allowed; }

/* ── Remove confirmation strip ───────────────────────────────── */
.remove-confirm-strip       { display:flex; align-items:center; gap:10px; background:#fef2f2; border:1px solid #fca5a5; border-radius:8px; padding:10px 13px; }
.remove-confirm-icon        { color:#dc2626; font-size:14px; flex-shrink:0; }
.remove-confirm-msg         { flex:1; font-size:13px; color:#991b1b; }
.remove-confirm-btns        { display:flex; gap:6px; flex-shrink:0; }
.btn-xs                     { display:inline-flex; align-items:center; gap:5px; padding:5px 14px; border-radius:6px; font-size:12px; font-weight:600; cursor:pointer; transition:background .15s; }
.btn-xs--outline            { background:transparent; border:1px solid #d1d5db; color:var(--ink); }
.btn-xs--outline:hover      { background:#f3f4f6; }
.btn-xs--danger             { background:#dc2626; border:1px solid #dc2626; color:#fff; }
.btn-xs--danger:hover       { background:#b91c1c; }
.btn-xs--danger:disabled    { opacity:.6; cursor:not-allowed; }

/* ── Export column picker ─────────────────────────────── */
.export-wrap { position:relative; display:inline-flex; }
.btn-export  { display:inline-flex; align-items:center; gap:6px; }
.export-menu { position:absolute; top:calc(100% + 4px); right:0; background:#fff; border:1px solid #dde5f0; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); z-index:200; min-width:160px; padding:6px; }
.export-item { display:flex; align-items:center; gap:8px; width:100%; padding:8px 12px; border:none; background:none; font-size:13px; color:#1a2b45; cursor:pointer; border-radius:6px; }
.export-item:hover { background:#f0f7ff; color:#004AAD; }
.col-picker-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1200; display:flex; align-items:center; justify-content:center; padding:16px; }
.col-picker-modal  { background:#fff; border-radius:16px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 24px 64px rgba(0,0,0,.18); }
.col-picker-header { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 0; }
.col-picker-title  { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub    { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close  { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; padding:4px 6px; flex-shrink:0; }
.col-picker-actions { display:flex; align-items:center; gap:8px; padding:14px 22px 0; }
.col-picker-link   { background:none; border:none; cursor:pointer; color:#004AAD; font-size:12px; font-weight:600; padding:0; }
.col-picker-sep    { color:#dde5f0; }
.col-picker-count  { margin-left:auto; font-size:12px; color:#7a90b0; }
.col-picker-grid   { display:grid; grid-template-columns:1fr 1fr; gap:4px; padding:14px 22px; }
.col-picker-item   { display:flex; align-items:center; gap:8px; padding:8px 10px; border-radius:8px; cursor:pointer; border:1px solid #f0f4f8; }
.col-picker-item:hover { background:#f0f7ff; border-color:#c7d8ef; }
.col-picker-check  { cursor:pointer; accent-color:#004AAD; width:15px; height:15px; flex-shrink:0; }
.col-picker-label  { font-size:13px; color:#1a2b45; font-weight:500; }
.col-picker-footer { display:flex; justify-content:flex-end; gap:8px; padding:14px 22px 20px; border-top:1px solid #f0f4f8; margin-top:8px; }
.fade-enter-active,.fade-leave-active { transition:opacity .18s; }
.fade-enter,.fade-leave-to { opacity:0; }

/* ── Per-page + Pagination ─────────────────────────────── */
.per-page-wrap    { display:flex; align-items:center; gap:6px; }
.per-page-label   { font-size:12px; color:var(--ink-faint); white-space:nowrap; }
.per-page-select  { height:34px; padding:0 10px; border:1px solid var(--line); border-radius:8px; font-size:13px; color:var(--ink); background:#fff; outline:none; }
.pagination-bar   { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; padding:14px 4px; margin-top:8px; }
.pagination-info  { font-size:13px; color:var(--ink-faint); }
.pagination-controls { display:flex; align-items:center; gap:4px; }
.pg-btn  { background:#fff; border:1px solid var(--line); border-radius:6px; width:32px; height:32px; font-size:13px; cursor:pointer; display:flex; align-items:center; justify-content:center; color:var(--ink); }
.pg-btn:hover:not(:disabled) { background:var(--accent-soft); border-color:var(--accent); color:var(--accent); }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-pages { display:flex; align-items:center; gap:2px; }
.pg-num   { background:#fff; border:1px solid var(--line); border-radius:6px; width:32px; height:32px; font-size:13px; cursor:pointer; display:flex; align-items:center; justify-content:center; color:var(--ink); }
.pg-num:hover    { background:var(--accent-soft); border-color:var(--accent); color:var(--accent); }
.pg-num--active  { background:var(--accent); border-color:var(--accent); color:#fff; font-weight:700; }

</style>
